import { useState, useEffect, useRef, useMemo, memo, useCallback, useDeferredValue } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { cachedAxiosGet } from '../utils/apiCache';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { AreaChart, BarChart, Area, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { getBackendUrl } from '../config';
import { normalizeDisplayPowerUnit, powerEnergyLabels, chartPowerTickLabel, chartEnergyDeltaTickLabel } from '../utils/displayPowerUnit';
import EnergyPowerFlowDiagram from '../components/EnergyPowerFlowDiagram';
import EnergyMeterSolidSolarFlow from '../components/EnergyMeterSolidSolarFlow';
import {
  energyMeterBrandFromApi,
  formatEnergyMeterChintNamingForDisplay,
  buildEnergyMeterDetailPath,
  isChintEnergyMeterRecord as isChintEnergyMeter,
} from '../utils/energyMeterBrandDisplay';
import { mergeEnergyMeterAlarmResponses } from '../utils/energyMeterAlarmsMerge';

const BACKEND_URL = getBackendUrl();

function displayEnergyMeterModelUi(meter) {
  const raw = meter?.model;
  if (raw != null && String(raw).trim() !== '') return formatEnergyMeterChintNamingForDisplay(String(raw));
  return 'DTSU666';
}
const CHART_CACHE_TTL_MS = 2 * 60 * 1000;

/** Diagrama e rrjedhjes së energjisë — vetëm për kompaninë HIB (emri përmban "HIB", pa dallim mëdha të vogla). */
function isHibEnergyFlowCompany(company) {
  const name = (company?.name ?? '').toString();
  return name.toUpperCase().includes('HIB');
}

// Siguron që .toFixed të mos thirret kur vlera mungon ose nuk është numër
const safeToFixed = (v, decimals = 2) =>
  v != null && Number.isFinite(Number(v)) ? Number(v).toFixed(decimals) : '0.00';

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

function clampEmChartDate(ymd, period) {
  const t = todayStr();
  if (!ymd || typeof ymd !== 'string') return t;
  if (period === 'Day') return ymd > t ? t : ymd;
  if (period === 'Month') {
    const tm = t.slice(0, 7);
    return ymd.slice(0, 7) > tm ? `${tm}-01` : ymd;
  }
  if (period === 'Year') {
    const y = Number(ymd.slice(0, 4));
    const ty = new Date().getFullYear();
    return y > ty ? `${ty}-01-01` : ymd;
  }
  return ymd;
}

// Format alarm for display: "Alarm Input 1 (DI1): OFF" – si DataLogger
const formatAlarmDisplay = (a) => {
  const raw = (a?.name ?? a?.message ?? a?.description ?? '').trim();
  const name = raw
    ? raw.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    : 'Alarm';
  const state = (a?.state ?? 'OFF').toUpperCase();
  return `${name}: ${state}`;
};

// Lista fikse e 4 alarmeve EM (DI1–DI4) – shfaqet gjithmonë, si te Inverterat
const EM_ALARMS_DEFAULT = [
  { no: 1, name: 'Alarm Input 1 (DI1)', state: 'OFF' },
  { no: 2, name: 'Alarm Input 2 (DI2)', state: 'OFF' },
  { no: 3, name: 'Alarm Input 3 (DI3)', state: 'OFF' },
  { no: 4, name: 'Alarm Input 4 (DI4)', state: 'OFF' },
];

function getDisplayAlarms(alarms) {
  return EM_ALARMS_DEFAULT.map((def) => {
    const fromPi = alarms?.find((a) => a?.no === def.no || String(a?.name || '').includes(`DI${def.no}`) || String(a?.name || '').includes(`(DI${def.no})`));
    return fromPi ? { ...def, state: fromPi.state ?? 'OFF' } : def;
  });
}

// ---------- Helpers për të dhëna nga pi_energy_meter_readings ----------
function flattenPayload(obj, prefix = '') {
  const out = {};
  if (!obj || typeof obj !== 'object') return out;
  for (const [k, v] of Object.entries(obj)) {
    const key = (prefix ? prefix + '_' : '') + String(k).replace(/\s/g, '_').toLowerCase();
    if (v != null && typeof v === 'object' && !Array.isArray(v)) {
      Object.assign(out, flattenPayload(v, key));
    } else {
      out[key] = v;
      out[k] = v;
    }
  }
  return out;
}

function getPayloadValue(flat, keys, fallback = '—') {
  for (const k of keys) {
    if (flat[k] != null) return String(flat[k]);
    const lower = k.toLowerCase();
    if (flat[lower] != null) return String(flat[lower]);
    if (flat[k.replace(/\s/g, '_')] != null) return String(flat[k.replace(/\s/g, '_')]);
  }
  return fallback;
}

const ENERGY_METER_READING_KEYS = {
  active_power: ['real_power', 'active_power', 'power', 'total_active_power', 'pac'],
  reactive_power: ['reactive_power', 'q_total', 'reactive'],
  apparent_power: ['apparent_power', 's_total', 'va_total'],
  power_factor: ['power_factor_total', 'power_factor', 'pf'],
  total_real_power: ['total_real_power', 'real_power', 'pac_total', 'ptot'],
  total_export: ['energy_delivered_mwh', 'total_export', 'exported_energy', 'export_total', 'e_export', 'energy_delivered'],
  total_import: ['energy_received_mwh', 'total_import', 'imported_energy', 'import_total', 'e_import', 'energy_received'],
  reactive_export: ['reactive_energy_delivered', 'q_export', 'energy_delivered_mvarh'],
  reactive_import: ['reactive_energy_received', 'q_import', 'qvarh_received'],
  apparent_energy: ['apparent_energy', 'va_total', 's_total', 'demand_apparent_present'],
  today_energy: ['today_energy', 'e_today', 'energy_today'],
  monthly_energy: ['monthly_energy', 'e_month', 'energy_month'],
  total_energy: ['total_energy', 'e_total', 'energy_total'],
  frequency: ['frequency', 'freq', 'hz'],
  voltage_a: ['van', 'voltage_a', 'ua', 'phase_voltage_ua', 'voltage_ua'],
  voltage_b: ['vbn', 'voltage_b', 'ub', 'phase_voltage_ub', 'voltage_ub'],
  voltage_c: ['vcn', 'voltage_c', 'uc', 'phase_voltage_uc', 'voltage_uc'],
  voltage_ab: ['vab', 'uab', 'line_voltage_uab', 'voltage_ab'],
  voltage_bc: ['vbc', 'ubc', 'line_voltage_ubc', 'voltage_bc'],
  voltage_ca: ['vca', 'uca', 'line_voltage_uca', 'voltage_ca'],
  vll_avg: ['vll_avg', 'line_voltage_avg', 'uab_avg'],
  vln_avg: ['vln_avg', 'phase_voltage_avg', 'van_avg'],
  current_a: ['ia', 'current_a', 'phase_current_ia', 'current_ia'],
  current_b: ['ib', 'current_b', 'phase_current_ib', 'current_ib'],
  current_c: ['ic', 'current_c', 'phase_current_ic', 'current_ic'],
  current_avg: ['i_average', 'current_avg', 'i_avg'],
  neutral_current: ['in', 'neutral_current', 'in_neutral'],
  ground_current: ['ground_current_ig', 'ig', 'ground_current', 'current_ground'],
  peak_real_power: ['peak_real_power', 'peak_power', 'max_power', 'pac_peak', 'demand_real_present'],
  peak_current: ['peak_current', 'max_current', 'i_peak', 'demand_ia_present', 'demand_ib_present', 'demand_ic_present'],
  demand_power: ['demand_real_present', 'demand_power', 'power_demand_15min'],
  peak_datetime: ['peak_datetime', 'peak_time', 'max_time'],
  energy_reset_datetime: ['energy_reset_datetime', 'date_time_last_reset', 'last_reset'],
  min_values: ['min_values', 'minimum'],
  max_values: ['max_values', 'maximum'],
  thd: ['thd_total', 'thd', 'total_harmonic_distortion', 'thd_v_total'],
  di1: ['di1_base_unit', 'di1', 'di1_base_unit'],
  di2: ['di2'],
  di3: ['di3'],
  di4: ['di4'],
  do1: ['do1'],
  do2: ['do2'],
};

/** Lexim pa meter të plotë: Pi dërgon `chint_energy_kwh` / `modbus_profile` në payload (jo gjithmonë në flat). */
function isChintPayload(payload) {
  if (!payload || typeof payload !== 'object') return false;
  if (String(payload.modbus_profile ?? '').toLowerCase().includes('chint')) return true;
  const ek = payload.chint_energy_kwh;
  if (ek != null && typeof ek === 'object' && !Array.isArray(ek)) return true;
  const flat = flattenPayload(payload);
  if (String(flat.modbus_profile ?? '').toLowerCase().includes('chint')) return true;
  return false;
}

function isChintMeterOrPayload(meter, payload) {
  return isChintEnergyMeter(meter) || isChintPayload(payload);
}

/** Një inverter: fuqia aktive në kW (payload pi_inverter_readings). */
function getInverterActivePowerKw(payload) {
  if (!payload || typeof payload !== 'object') return 0;
  const flat = flattenPayload(payload);
  const kwKeys = ['active_power_kw', 'p_ac_kw', 'pac_kw'];
  for (const key of kwKeys) {
    const v = flat[key];
    if (v != null && v !== '' && Number.isFinite(Number(v))) return Number(v);
    const lk = String(key).toLowerCase();
    if (flat[lk] != null && flat[lk] !== '' && Number.isFinite(Number(flat[lk]))) return Number(flat[lk]);
  }
  const pac = flat.pac ?? flat.active_power;
  if (pac != null && pac !== '' && Number.isFinite(Number(pac))) {
    const n = Number(pac);
    if (Math.abs(n) >= 10000) return n / 1000;
    return n;
  }
  return 0;
}

/**
 * Import / rrjet për diagramë (kW): Chint → pt_w (W); të tjerët → real_power (ose fallback).
 * Load = Ppv + Import (mbledhje/zbritje sipas shenjës).
 */
function getImportPowerKwForFlow(rawPayload, meter) {
  const flat = flattenPayload(rawPayload || {});
  if (isChintEnergyMeter(meter)) {
    for (const k of ['pt_w', 'Pt_W', 'PT_W', 'pt_W']) {
      const raw = flat[k] ?? flat[String(k).toLowerCase()];
      if (raw != null && raw !== '' && Number.isFinite(Number(raw))) return Number(raw) / 1000;
    }
  }
  const keys = ['real_power', 'active_power', 'power', 'total_active_power', 'pac'];
  for (const k of keys) {
    const v = flat[k];
    if (v != null && v !== '' && Number.isFinite(Number(v))) {
      const n = Number(v);
      if (Math.abs(n) >= 10000) return n / 1000;
      return n;
    }
  }
  return 0;
}

/** Ppv = shuma e inverterëve (kW) kur kalon inverterSumKw; përndryshe fallback nga payload. Load = Ppv + Import. */
function buildPowerFlowDiagramValues(meter, rawPayload, inverterSumKw) {
  const flat = flattenPayload(rawPayload || {});
  const num = (keys) => {
    for (const k of keys) {
      const v = flat[k];
      if (v != null && v !== '' && Number.isFinite(Number(v))) return Number(v);
      const low = String(k).toLowerCase();
      if (flat[low] != null && flat[low] !== '' && Number.isFinite(Number(flat[low]))) return Number(flat[low]);
    }
    return 0;
  };
  let ppvKw = 0;
  if (inverterSumKw != null && Number.isFinite(inverterSumKw)) {
    ppvKw = inverterSumKw;
  } else {
    ppvKw = num(['ppv_kw', 'pv_power_kw', 'p_pv_kw', 'pv_power', 'ppv']);
  }
  const importKw = getImportPowerKwForFlow(rawPayload, meter);
  const loadKw = ppvKw + importKw;
  return {
    ppvKw,
    batteryKw: num(['battery_discharge_kw', 'battery_power_kw', 'ess_discharge_kw', 'bat_power_kw', 'battery_kw']),
    loadKw,
    gridImportKw: importKw,
  };
}

/** kW PV nga payload ose shuma e inverterëve të centralës. */
function getSolarKwFromPayload(payload, plantInverterSumKw) {
  const flat = flattenPayload(payload || {});
  const keys = ['ppv_kw', 'pv_power_kw', 'p_pv_kw', 'pv_power', 'ppv', 'solar_power_kw'];
  for (const k of keys) {
    const v = flat[k];
    if (v != null && v !== '' && Number.isFinite(Number(v))) {
      const n = Number(v);
      return Math.abs(n) >= 10000 ? n / 1000 : n;
    }
  }
  if (plantInverterSumKw != null && Number.isFinite(plantInverterSumKw)) return Math.max(0, plantInverterSumKw);
  return 0;
}

function getDischargeKwFromPayload(payload) {
  const flat = flattenPayload(payload || {});
  for (const k of ['battery_discharge_kw', 'ess_discharge_kw', 'discharge_kw', 'ess_discharge_power_kw']) {
    const v = flat[k];
    if (v != null && Number.isFinite(Number(v)) && Number(v) > 0) return Number(v);
  }
  for (const k of ['battery_power_kw', 'bat_power_kw', 'battery_kw', 'ess_power_kw']) {
    const v = flat[k];
    if (v != null && Number.isFinite(Number(v)) && Number(v) > 0) return Number(v);
  }
  return 0;
}

function getChargeKwFromPayload(payload) {
  const flat = flattenPayload(payload || {});
  for (const k of ['battery_charge_kw', 'ess_charge_kw', 'charge_kw', 'ess_charge_power_kw']) {
    const v = flat[k];
    if (v != null && Number.isFinite(Number(v)) && Number(v) > 0) return Number(v);
  }
  for (const k of ['battery_power_kw', 'battery_kw']) {
    const v = flat[k];
    if (v != null && Number.isFinite(Number(v)) && Number(v) < 0) return Math.abs(Number(v));
  }
  return 0;
}

function buildReadingsFromPayload(payload, meter = null) {
  const flat = flattenPayload(payload || {});
  const get = (keyList, fallback = '—') => getPayloadValue(flat, keyList, fallback);
  let pfRaw = get(ENERGY_METER_READING_KEYS.power_factor, '0');
  const pfNum = Number(pfRaw);
  const power_factor = (pfNum >= -1 && pfNum <= 1 && !Number.isNaN(pfNum)) ? pfRaw : '—';
  const exMwh = getGridExportEnergyMwhFromPayload(payload, meter);
  const imMwh = getGridImportEnergyMwhFromPayload(payload, meter);
  return {
    active_power: get(ENERGY_METER_READING_KEYS.active_power, '0'),
    reactive_power: get(ENERGY_METER_READING_KEYS.reactive_power, '0'),
    apparent_power: get(ENERGY_METER_READING_KEYS.apparent_power, '—'),
    power_factor,
    total_real_power: get(ENERGY_METER_READING_KEYS.total_real_power, '—'),
    total_export: Number.isFinite(exMwh) ? String(exMwh) : get(ENERGY_METER_READING_KEYS.total_export, '0'),
    total_import: Number.isFinite(imMwh) ? String(imMwh) : get(ENERGY_METER_READING_KEYS.total_import, '0'),
    reactive_export: get(ENERGY_METER_READING_KEYS.reactive_export, '—'),
    reactive_import: get(ENERGY_METER_READING_KEYS.reactive_import, '—'),
    apparent_energy: get(ENERGY_METER_READING_KEYS.apparent_energy, '—'),
    today_energy: get(ENERGY_METER_READING_KEYS.today_energy, '0'),
    monthly_energy: get(ENERGY_METER_READING_KEYS.monthly_energy, '0'),
    total_energy: get(ENERGY_METER_READING_KEYS.total_energy, '0'),
    frequency: get(ENERGY_METER_READING_KEYS.frequency, '0'),
    voltage_a: get(ENERGY_METER_READING_KEYS.voltage_a, '—'),
    voltage_b: get(ENERGY_METER_READING_KEYS.voltage_b, '—'),
    voltage_c: get(ENERGY_METER_READING_KEYS.voltage_c, '—'),
    voltage_ab: get(ENERGY_METER_READING_KEYS.voltage_ab, '—'),
    voltage_bc: get(ENERGY_METER_READING_KEYS.voltage_bc, '—'),
    voltage_ca: get(ENERGY_METER_READING_KEYS.voltage_ca, '—'),
    vll_avg: get(ENERGY_METER_READING_KEYS.vll_avg, '—'),
    vln_avg: get(ENERGY_METER_READING_KEYS.vln_avg, '—'),
    current_a: get(ENERGY_METER_READING_KEYS.current_a, '—'),
    current_b: get(ENERGY_METER_READING_KEYS.current_b, '—'),
    current_c: get(ENERGY_METER_READING_KEYS.current_c, '—'),
    current_avg: get(ENERGY_METER_READING_KEYS.current_avg, '—'),
    neutral_current: get(ENERGY_METER_READING_KEYS.neutral_current, '—'),
    ground_current: get(ENERGY_METER_READING_KEYS.ground_current, '—'),
    peak_real_power: get(ENERGY_METER_READING_KEYS.peak_real_power, '—'),
    peak_current: get(ENERGY_METER_READING_KEYS.peak_current, '—'),
    demand_power: get(ENERGY_METER_READING_KEYS.demand_power, '—'),
    peak_datetime: get(ENERGY_METER_READING_KEYS.peak_datetime, '—'),
    energy_reset_datetime: get(ENERGY_METER_READING_KEYS.energy_reset_datetime, '—'),
    min_values: get(ENERGY_METER_READING_KEYS.min_values, '—'),
    max_values: get(ENERGY_METER_READING_KEYS.max_values, '—'),
    thd: get(ENERGY_METER_READING_KEYS.thd, '—'),
    di1: get(ENERGY_METER_READING_KEYS.di1, '—'),
    di2: get(ENERGY_METER_READING_KEYS.di2, '—'),
    di3: get(ENERGY_METER_READING_KEYS.di3, '—'),
    di4: get(ENERGY_METER_READING_KEYS.di4, '—'),
    do1: get(ENERGY_METER_READING_KEYS.do1, '—'),
    do2: get(ENERGY_METER_READING_KEYS.do2, '—'),
  };
}

function formatTimeAgo(ts) {
  if (!ts) return null;
  const d = new Date(ts);
  if (isNaN(d.getTime())) return null;
  const sec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)} min ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)} h ago`;
  return `${Math.floor(sec / 86400)} days ago`;
}

const STALE_READING_SEC = 300; // 5 min – mbi këtë shfaq Offline

function getExportFromPayload(payload) {
  if (!payload || typeof payload !== 'object') return 0;
  const flat = flattenPayload(payload);
  const keys = ['energy_delivered_mwh', 'energy_delivered', 'total_export', 'exported_energy', 'export_total', 'e_export'];
  for (const k of keys) {
    const v = flat[k];
    if (v != null && v !== '') {
      const n = Number(v);
      if (!Number.isNaN(n)) return n;
    }
  }
  return 0;
}

function getImportFromPayload(payload) {
  if (!payload || typeof payload !== 'object') return 0;
  const flat = flattenPayload(payload);
  const keys = ['energy_received_mwh', 'total_import', 'imported_energy', 'import_total', 'e_import'];
  for (const k of keys) {
    const v = flat[k];
    if (v != null && v !== '') {
      const n = Number(v);
      if (!Number.isNaN(n)) return n;
    }
  }
  return 0;
}

/**
 * MWh numërues — eksport drejt rrjetit (semantik UI).
 * Schneider/PM: `energy_delivered_mwh` ≈ eksport, `energy_received_mwh` ≈ import.
 * Chint (Pi `chint_raw_to_pm_payload`): ImpEp → `energy_delivered_mwh`, ExpEp → `energy_received_mwh` — kundërt me emrin.
 */
function getGridExportEnergyMwhFromPayload(payload, meter) {
  if (!payload || typeof payload !== 'object') return 0;
  if (isChintMeterOrPayload(meter, payload)) return getImportFromPayload(payload);
  return getExportFromPayload(payload);
}

function getGridImportEnergyMwhFromPayload(payload, meter) {
  if (!payload || typeof payload !== 'object') return 0;
  if (isChintMeterOrPayload(meter, payload)) return getExportFromPayload(payload);
  return getImportFromPayload(payload);
}

/** Chint: fuqia totale aktive në W (`chint_measurements.Pt_W`); null nëse mungon. */
function getChintPtWFromPayload(payload) {
  if (!payload || typeof payload !== 'object') return null;
  const cm = payload.chint_measurements;
  if (cm && typeof cm === 'object' && !Array.isArray(cm)) {
    const raw = cm.Pt_W ?? cm.pt_w ?? cm.PT_W;
    if (raw != null && raw !== '' && Number.isFinite(Number(raw))) return Number(raw);
  }
  const flat = flattenPayload(payload);
  const nested = flat.chint_measurements_pt_w;
  if (nested != null && nested !== '' && Number.isFinite(Number(nested))) return Number(nested);
  const short = flat.pt_w ?? flat.Pt_W ?? flat.PT_W;
  if (short != null && short !== '' && Number.isFinite(Number(short))) return Number(short);
  return null;
}

// Active Power në MW – |MW| për PV / fuqi aktive në UI
function getActivePowerMW(payload, meter = null) {
  if (!payload || typeof payload !== 'object') return 0;
  const useChintPt =
    isChintMeterOrPayload(meter, payload) ||
    (payload.chint_measurements != null && typeof payload.chint_measurements === 'object');
  if (useChintPt) {
    const w = getChintPtWFromPayload(payload);
    if (w != null && Number.isFinite(w)) return Math.abs(w) / 1_000_000;
  }
  const flat = flattenPayload(payload);
  const keys = [
    'real_power',
    'active_power',
    'power',
    'total_active_power',
    'pac',
    'measurements_real_power',
    'measurements_active_power',
    'measurements_total_active_power',
  ];
  for (const k of keys) {
    const v = flat[k];
    if (v != null && v !== '') {
      const n = Number(v);
      if (!Number.isNaN(n)) {
        if (n >= 100) return Math.abs(n / 1000);
        return Math.abs(n);
      }
    }
  }
  return 0;
}

/** real_power në MW me shenjë (jo abs). Skala si getActivePowerMW. */
function getSignedRealPowerMw(payload) {
  if (!payload || typeof payload !== 'object') return 0;
  const flat = flattenPayload(payload);
  const keys = ['real_power', 'active_power', 'total_real_power', 'power'];
  for (const k of keys) {
    const v = flat[k];
    if (v != null && v !== '') {
      const n = Number(v);
      if (!Number.isNaN(n)) {
        if (Math.abs(n) >= 100) return n / 1000;
        return n;
      }
    }
  }
  return 0;
}

/**
 * Konventë tipike Schneider: MW negative = eksport drejt rrjetit, MW positive = import.
 * Chint: Pt_W (W); nëse mungon → 0 për eksport të menjëhershëm.
 */
function getInstantNetExportKw(reading, meter) {
  const p = reading?.payload;
  if (!p) return 0;
  if (isChintMeterOrPayload(meter, p)) {
    const w = getChintPtWFromPayload(p);
    if (w != null && Number.isFinite(w)) {
      return Math.max(0, -w / 1000);
    }
    return 0;
  }
  const mw = getSignedRealPowerMw(p);
  return Math.max(0, -mw) * 1000;
}

function getInstantNetImportKw(reading, meter) {
  const pl = reading?.payload;
  if (!pl) return 0;
  if (isChintMeterOrPayload(meter, pl)) {
    return Math.max(0, getImportPowerKwForFlow(pl, meter || {}));
  }
  const mw = getSignedRealPowerMw(pl);
  return Math.max(0, mw) * 1000;
}

/** Ngarkesë në kW nëse ka regjistër; përndryshe Ppv + import (diagram). */
function getSiteLoadKwFromPayload(payload, meter, plantInverterSumKw) {
  const flat = flattenPayload(payload || {});
  const keys = ['site_load_kw', 'load_kw', 'total_load_kw', 'load_power_kw', 'p_load', 'consumer_load_kw', 'total_load_active_kw'];
  for (const k of keys) {
    const v = flat[k];
    if (v != null && v !== '' && Number.isFinite(Number(v))) {
      const n = Number(v);
      return Math.abs(n) >= 10000 ? n / 1000 : Math.abs(n);
    }
  }
  return buildPowerFlowDiagramValues(meter, payload, plantInverterSumKw).loadKw;
}

/** kW nga delta numëruesi eksporti (semantik, përfshi Chint). */
function getGridExportSlopeKw(prevReading, nextReading, meter) {
  if (!prevReading?.ts || !nextReading?.ts) return 0;
  const t0 = new Date(prevReading.ts).getTime();
  const t1 = new Date(nextReading.ts).getTime();
  const dtH = (t1 - t0) / 3_600_000;
  if (dtH <= 1e-9) return 0;
  const e0 = getGridExportEnergyMwhFromPayload(prevReading.payload, meter);
  const e1 = getGridExportEnergyMwhFromPayload(nextReading.payload, meter);
  const dMwh = e1 - e0;
  if (dMwh <= 0) return 0;
  return (dMwh * 1000) / dtH;
}

/** kW nga delta numëruesi importit (semantik, përfshi Chint). */
function getGridImportSlopeKw(prevReading, nextReading, meter) {
  if (!prevReading?.ts || !nextReading?.ts) return 0;
  const t0 = new Date(prevReading.ts).getTime();
  const t1 = new Date(nextReading.ts).getTime();
  const dtH = (t1 - t0) / 3_600_000;
  if (dtH <= 1e-9) return 0;
  const e0 = getGridImportEnergyMwhFromPayload(prevReading.payload, meter);
  const e1 = getGridImportEnergyMwhFromPayload(nextReading.payload, meter);
  const dMwh = e1 - e0;
  if (dMwh <= 0) return 0;
  return (dMwh * 1000) / dtH;
}

// Llogarit Today Energy, Monthly Energy nga readings (delta i numëruesit të eksportit semantik)
function computeEnergyFromReadings(readingsList, period, meter = null) {
  if (!readingsList || readingsList.length < 2) return null;
  const sorted = [...readingsList].sort((a, b) => new Date(a.ts) - new Date(b.ts));
  const now = new Date();
  let startDate;
  let endDate;
  if (period === 'today') {
    startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    endDate = new Date();
  } else if (period === 'month') {
    startDate = new Date(now.getFullYear(), now.getMonth(), 1);
    endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
  } else {
    return null;
  }
  const inRange = sorted.filter((r) => {
    const rt = r.ts ? new Date(r.ts) : null;
    return rt && rt >= startDate && rt <= endDate;
  });
  if (inRange.length < 2) return null;
  const first = getGridExportEnergyMwhFromPayload(inRange[0].payload, meter);
  const last = getGridExportEnergyMwhFromPayload(inRange[inRange.length - 1].payload, meter);
  const delta = last - first;
  return delta >= 0 ? delta : 0;
}

function isWithinProductionHours(d) {
  const h = d.getHours();
  return h >= 6 && h <= 20;
}

function createEmptyEmDayRow(timeStr) {
  return {
    name: timeStr,
    time: timeStr,
    value: 0,
    valueMwh: 0,
    exportToGrid: 0,
    importFromGrid: 0,
    solarProduction: 0,
    systemProduction: 0,
    loadConsumption: 0,
    selfConsumption: 0,
    charged: 0,
    discharged: 0,
    importMwh: 0,
    selfMwhApprox: 0,
  };
}

/** Fuqia mesatare (kW) nga numërimuesi MWh në interval 5 min — e njëjta logjikë si eksporti / importi. */
function getDayBucketPowerKwFromMwhCounter(sorted, start, end, opts) {
  const { getMwhFromReading, getSlopeKw, getInstantFallbackKw } = opts;
  const inBucket = sorted.filter((r) => {
    const rt = r.ts ? new Date(r.ts) : null;
    return rt && rt >= start && rt <= end;
  });
  const hours5min = 5 / 60;

  const maxInstantKwIn = (readings) => {
    let mx = 0;
    for (const r of readings) {
      const kw = getInstantFallbackKw(r);
      if (Number.isFinite(kw) && kw > mx) mx = kw;
    }
    return mx;
  };

  let prevBeforeStart = null;
  for (let i = sorted.length - 1; i >= 0; i -= 1) {
    const rt = sorted[i].ts ? new Date(sorted[i].ts) : null;
    if (rt && rt < start) {
      prevBeforeStart = sorted[i];
      break;
    }
  }

  let nextAfterEnd = null;
  for (let i = 0; i < sorted.length; i += 1) {
    const rt = sorted[i].ts ? new Date(sorted[i].ts) : null;
    if (rt && rt > end) {
      nextAfterEnd = sorted[i];
      break;
    }
  }

  if (inBucket.length >= 2) {
    const first = getMwhFromReading(inBucket[0]);
    const last = getMwhFromReading(inBucket[inBucket.length - 1]);
    const deltaMwh = Math.max(0, last - first);
    let powerKw = hours5min > 0 ? (deltaMwh * 1000) / hours5min : 0;
    if (deltaMwh < 1e-12) {
      const inst = maxInstantKwIn(inBucket);
      if (inst > 0) powerKw = inst;
    }
    return powerKw;
  }
  if (inBucket.length === 1) {
    const row = inBucket[0];
    let powerKw = maxInstantKwIn(inBucket);
    if (powerKw < 1e-6 && prevBeforeStart) {
      const slope = getSlopeKw(prevBeforeStart, row);
      if (slope > 0) powerKw = slope;
    }
    return powerKw;
  }

  if (prevBeforeStart && nextAfterEnd) {
    const between = getSlopeKw(prevBeforeStart, nextAfterEnd);
    if (between > 0) return between;
  }
  if (prevBeforeStart) {
    const kw = getInstantFallbackKw(prevBeforeStart);
    if (Number.isFinite(kw) && kw > 0) return kw;
    if (nextAfterEnd) {
      const slope = getSlopeKw(prevBeforeStart, nextAfterEnd);
      if (slope > 0) return slope;
    }
  }
  return 0;
}

function computeEmEnergyAnalysisDayTotals(rows) {
  if (!rows?.length || rows[0].importMwh === undefined) return null;
  let exportKwh = 0;
  let importKwh = 0;
  let selfKwh = 0;
  for (const row of rows) {
    exportKwh += Number(row.valueMwh || 0) * 1000;
    importKwh += Number(row.importMwh || 0) * 1000;
    selfKwh += Number(row.selfMwhApprox || 0) * 1000;
  }
  const systemTotal = selfKwh + exportKwh;
  const loadTotal = selfKwh + importKwh;
  return {
    exportKwh,
    importKwh,
    selfKwh,
    systemTotal,
    loadTotal,
    selfPctOfSystem: systemTotal > 1e-6 ? (selfKwh / systemTotal) * 100 : 0,
    exportPctOfSystem: systemTotal > 1e-6 ? (exportKwh / systemTotal) * 100 : 0,
    selfPctOfLoad: loadTotal > 1e-6 ? (selfKwh / loadTotal) * 100 : 0,
    importPctOfLoad: loadTotal > 1e-6 ? (importKwh / loadTotal) * 100 : 0,
  };
}

function formatEmKwhSummary(kwh) {
  if (!Number.isFinite(kwh)) return '—';
  if (Math.abs(kwh) >= 100) return `${kwh.toFixed(0)} kWh`;
  return `${kwh.toFixed(1)} kWh`;
}

const EM_ENERGY_ANALYSIS_SERIES = [
  { id: 'systemProduction', labelKey: 'emSeriesSystemProduction', color: '#38bdf8', dataKey: 'systemProduction' },
  { id: 'solarProduction', labelKey: 'emSeriesSolarProduction', color: '#22c55e', dataKey: 'solarProduction' },
  { id: 'loadConsumption', labelKey: 'emSeriesLoadConsumption', color: '#f87171', dataKey: 'loadConsumption' },
  { id: 'selfConsumption', labelKey: 'emSeriesSelfConsumption', color: '#a855f7', dataKey: 'selfConsumption' },
  { id: 'exportToGrid', labelKey: 'emSeriesExportToGrid', color: '#3b82f6', dataKey: 'exportToGrid' },
  { id: 'importFromGrid', labelKey: 'emSeriesImportFromGrid', color: '#fb923c', dataKey: 'importFromGrid' },
  { id: 'charged', labelKey: 'emSeriesCharged', color: '#06b6d4', dataKey: 'charged' },
  { id: 'discharged', labelKey: 'emSeriesDischarged', color: '#4ade80', dataKey: 'discharged' },
];

function getEmEnergyAnalysisSeriesForMeter() {
  return EM_ENERGY_ANALYSIS_SERIES;
}

const defaultEmAnalysisSeriesVisible = () =>
  EM_ENERGY_ANALYSIS_SERIES.reduce((acc, s) => {
    acc[s.id] = true;
    return acc;
  }, {});

/** Për kompani jo-HIB: vetëm seria «Export to grid» në grafik/tooltip (pa legjendë me checkbox). */
const EM_ANALYSIS_VISIBLE_EXPORT_ONLY = EM_ENERGY_ANALYSIS_SERIES.reduce((acc, s) => {
  acc[s.id] = s.id === 'exportToGrid';
  return acc;
}, {});

// Grafiku si Inverter/DataLogger: Day (00:00–23:59), Month (1–sot), Year (Jan–muaji aktual).
function buildEnergyMeterChartData(readingsList, period, selectedDate = null, meter = null, plantInverterSumKw = null) {
  if (!readingsList || !readingsList.length) return [{ name: '—', value: 0 }];
  const refDate = selectedDate ? new Date(selectedDate) : new Date();
  const now = new Date();
  const sorted = [...readingsList].sort((a, b) => new Date(a.ts) - new Date(b.ts));
  const meterSafe = meter && typeof meter === 'object' ? meter : {};

  const getDeltaForBucket = (start, end) => {
    const inBucket = sorted.filter((r) => {
      const rt = r.ts ? new Date(r.ts) : null;
      return rt && rt >= start && rt <= end;
    });
    if (inBucket.length < 2) return 0;
    const first = getGridExportEnergyMwhFromPayload(inBucket[0].payload, meterSafe);
    const last = getGridExportEnergyMwhFromPayload(inBucket[inBucket.length - 1].payload, meterSafe);
    return Math.max(0, last - first);
  };

  const getDayBucketPowerKw = (start, end) =>
    getDayBucketPowerKwFromMwhCounter(sorted, start, end, {
      getMwhFromReading: (r) => getGridExportEnergyMwhFromPayload(r.payload, meterSafe),
      getSlopeKw: (a, b) => getGridExportSlopeKw(a, b, meterSafe),
      getInstantFallbackKw: (r) => getInstantNetExportKw(r, meterSafe),
    });

  const getDayBucketImportPowerKw = (start, end) =>
    getDayBucketPowerKwFromMwhCounter(sorted, start, end, {
      getMwhFromReading: (r) => getGridImportEnergyMwhFromPayload(r.payload, meterSafe),
      getSlopeKw: (a, b) => getGridImportSlopeKw(a, b, meterSafe),
      getInstantFallbackKw: (r) => getInstantNetImportKw(r, meterSafe),
    });

  const isToday = refDate.getFullYear() === now.getFullYear() && refDate.getMonth() === now.getMonth() && refDate.getDate() === now.getDate();
  const isCurrentMonth = refDate.getFullYear() === now.getFullYear() && refDate.getMonth() === now.getMonth();
  const isCurrentYear = refDate.getFullYear() === now.getFullYear();

  if (period === 'Day') {
    const result = [];
    const endOfDay = new Date(refDate.getFullYear(), refDate.getMonth(), refDate.getDate(), 23, 59, 59);
    const stopAt = isToday ? now : endOfDay;
    for (let h = 0; h <= 23; h++) {
      for (let m = 0; m < 60; m += 5) {
        const start = new Date(refDate.getFullYear(), refDate.getMonth(), refDate.getDate(), h, m, 0);
        if (start > stopAt) return result.length ? result : [createEmptyEmDayRow('00:00')];
        const end = new Date(start.getTime() + 5 * 60 * 1000 - 1);
        const timeStr = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
        const exportPowerKw = getDayBucketPowerKw(start, end);
        const importPowerKw = getDayBucketImportPowerKw(start, end);
        const hours5min = 5 / 60;
        const deltaMwh = hours5min > 0 ? (exportPowerKw * hours5min) / 1000 : 0;
        const importMwh = hours5min > 0 ? (importPowerKw * hours5min) / 1000 : 0;

        const inBucket = sorted.filter((r) => {
          const rt = r.ts ? new Date(r.ts) : null;
          return rt && rt >= start && rt <= end;
        });
        let solarKw = 0;
        let loadKw = 0;
        let chargeKw = 0;
        let dischargeKw = 0;
        for (const r of inBucket) {
          const sk = getSolarKwFromPayload(r.payload, plantInverterSumKw);
          if (sk > solarKw) solarKw = sk;
          const lk = getSiteLoadKwFromPayload(r.payload, meterSafe, plantInverterSumKw);
          if (lk > loadKw) loadKw = lk;
          const ck = getChargeKwFromPayload(r.payload);
          if (ck > chargeKw) chargeKw = ck;
          const dk = getDischargeKwFromPayload(r.payload);
          if (dk > dischargeKw) dischargeKw = dk;
        }
        // Load = gjithë ngarkesa e vlerësuar; vetë-konsum ≈ min(PV, pjesa e ngarkesës jo nga importi i menjëhershëm).
        const loadFedWithoutGridImport = Math.max(0, loadKw - importPowerKw);
        const selfConsumptionKw = Math.min(Math.max(0, solarKw), Math.max(0, loadKw), loadFedWithoutGridImport);
        // Sistem: PV + bateri, ose kur ka rrjedhë në numërues më i madhi nga (eksport + vetë) që të mos përputhet gjithmonë me PV.
        const systemFromGen = Math.max(0, solarKw) + Math.max(0, dischargeKw);
        const systemFromMeterBalance = exportPowerKw + Math.min(selfConsumptionKw, solarKw);
        const systemKw =
          exportPowerKw > 1e-3 || importPowerKw > 1e-3
            ? Math.max(systemFromGen, systemFromMeterBalance)
            : systemFromGen;
        const selfMwhApprox = hours5min > 0 ? (selfConsumptionKw * hours5min) / 1000 : 0;

        result.push({
          name: timeStr,
          time: timeStr,
          value: exportPowerKw,
          valueMwh: deltaMwh,
          exportToGrid: exportPowerKw,
          importFromGrid: importPowerKw,
          solarProduction: solarKw,
          systemProduction: systemKw,
          loadConsumption: loadKw,
          selfConsumption: selfConsumptionKw,
          charged: chargeKw,
          discharged: dischargeKw,
          importMwh,
          selfMwhApprox,
        });
      }
    }
    return result.length ? result : [createEmptyEmDayRow('00:00')];
  }

  if (period === 'Month') {
    const daysInMonth = new Date(refDate.getFullYear(), refDate.getMonth() + 1, 0).getDate();
    const endDay = isCurrentMonth ? now.getDate() : daysInMonth;
    const result = [];
    for (let day = 1; day <= endDay; day++) {
      const start = new Date(refDate.getFullYear(), refDate.getMonth(), day);
      const end = new Date(start.getTime() + 24 * 60 * 60 * 1000 - 1);
      const nameStr = `${String(day).padStart(2, '0')}.${String(refDate.getMonth() + 1).padStart(2, '0')}`;
      result.push({ name: nameStr, time: nameStr, value: getDeltaForBucket(start, end) });
    }
    return result.length ? result : [{ name: '01', time: '01', value: 0 }];
  }

  if (period === 'Year') {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'Maj', 'Qer', 'Kor', 'Gus', 'Sht', 'Tet', 'Nën', 'Dhj'];
    const endMonth = isCurrentYear ? now.getMonth() : 11;
    const result = [];
    for (let m = 0; m <= endMonth; m++) {
      const start = new Date(refDate.getFullYear(), m, 1);
      const end = new Date(refDate.getFullYear(), m + 1, 0, 23, 59, 59);
      const nameStr = `${monthNames[m]} ${refDate.getFullYear()}`;
      result.push({ name: nameStr, time: nameStr, value: getDeltaForBucket(start, end) });
    }
    return result.length ? result : [{ name: monthNames[0], time: monthNames[0], value: 0 }];
  }

  if (period === 'Total') {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'Maj', 'Qer', 'Kor', 'Gus', 'Sht', 'Tet', 'Nën', 'Dhj'];
    const result = [];
    const now = new Date();
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const start = new Date(d.getFullYear(), d.getMonth(), 1);
      const end = new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59);
      const nameStr = `${monthNames[d.getMonth()]} ${d.getFullYear()}`;
      result.push({ name: nameStr, time: nameStr, value: getDeltaForBucket(start, end) });
    }
    return result;
  }

  return [{ name: '—', value: 0 }];
}

// ---------- Digital I/O: parse bool from readings ----------
function parseBool(val) {
  if (val === true || val === 'true' || val === 1 || val === '1') return true;
  if (val === false || val === 'false' || val === 0 || val === '0') return false;
  return null;
}

// ---------- Digital I/O Section: 4 inputs | 2 outputs; long-press popup për outputs ----------
const LONG_PRESS_MS = 5000;

function DigitalIOSection({ meter, readings, rawPayload, isDark, isMobile, fetchData, t = (k) => k }) {
  const [outputPopup, setOutputPopup] = useState(null); // { coil_num, value }
  const [password, setPassword] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [writing, setWriting] = useState(false);
  const pressTimerRef = useRef(null);
  const pressStartRef = useRef(null);

  const di1 = parseBool(readings?.di1);
  const di2 = parseBool(readings?.di2);
  const di3 = parseBool(readings?.di3);
  const di4 = parseBool(readings?.di4);
  const do1 = parseBool(readings?.do1);
  const do2 = parseBool(readings?.do2);

  const inputs = [
    { id: 'di1', name: 'DI1', val: di1 },
    { id: 'di2', name: 'DI2', val: di2 },
    { id: 'di3', name: 'DI3', val: di3 },
    { id: 'di4', name: 'DI4', val: di4 },
  ];
  const outputs = [
    { id: 'do1', num: 1, name: 'DO1', val: do1 },
    { id: 'do2', num: 2, name: 'DO2', val: do2 },
  ];

  const handleOutputPressStart = (coilNum, currentVal) => {
    pressStartRef.current = Date.now();
    pressTimerRef.current = setTimeout(() => {
      setOutputPopup({ coil_num: coilNum, value: !currentVal });
      setPassword('');
      setConfirmed(false);
    }, LONG_PRESS_MS);
  };
  const handleOutputPressEnd = () => {
    if (pressTimerRef.current) {
      clearTimeout(pressTimerRef.current);
      pressTimerRef.current = null;
    }
  };
  const handleWriteCoil = async () => {
    if (!outputPopup || !password || !confirmed) return;
    setWriting(true);
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        `${BACKEND_URL}/api/devices/energy-meters/${meter?.id}/write-coil`,
        { password, confirmed, coil_num: outputPopup.coil_num, value: outputPopup.value },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setOutputPopup(null);
      fetchData?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || t('errorGeneric'));
    } finally {
      setWriting(false);
    }
  };

  const cardCl = isDark ? 'bg-[#1e293b] border-slate-700' : 'bg-white border-gray-200';
  return (
    <div className={`mt-4 rounded-xl border p-4 ${cardCl}`}>
      <h3 className={`text-sm font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('digitalIO')}</h3>
      <div className="flex flex-wrap gap-4">
        <div className="flex-1 min-w-[140px]">
          <p className={`text-xs mb-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{t('inputs')}</p>
          <div className="flex gap-2 flex-wrap">
            {inputs.map(({ id, name, val }) => (
              <div key={id} className={`px-3 py-2 rounded-lg text-sm font-medium ${val === null ? 'bg-gray-500 dark:bg-slate-600 text-white' : val ? 'bg-green-600 text-white' : 'bg-red-600 text-white'}`}>
                {name}: {val === null ? '—' : val ? 'ON' : 'OFF'}
              </div>
            ))}
          </div>
        </div>
        <div className="flex-1 min-w-[140px]">
          <p className={`text-xs mb-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{t('outputs')}</p>
          <div className="flex gap-2 flex-wrap">
            {outputs.map(({ id, num, name, val }) => (
              <button
                key={id}
                onMouseDown={() => handleOutputPressStart(num, val)}
                onMouseUp={handleOutputPressEnd}
                onMouseLeave={handleOutputPressEnd}
                onTouchStart={() => handleOutputPressStart(num, val)}
                onTouchEnd={handleOutputPressEnd}
                className={`px-3 py-2 rounded-lg text-sm font-medium select-none ${val === null ? 'bg-gray-500 dark:bg-slate-600 text-white' : val ? 'bg-green-600 text-white' : 'bg-red-600 text-white'}`}
              >
                {name}: {val === null ? '—' : val ? 'ON' : 'OFF'}
              </button>
            ))}
          </div>
        </div>
      </div>
      {/* Zonë figurë dhe hapësirë bosh */}
      <div className={`mt-4 h-24 rounded-lg ${isDark ? 'bg-[#0f172a]' : 'bg-gray-100'} flex items-center justify-center`}>
        <span className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{t('figureScheme')}</span>
      </div>

      {/* Verifikim payload – çfarë vjen nga Pi (raw) */}
      {outputPopup && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className={`rounded-2xl p-4 w-full max-w-sm ${isDark ? 'bg-slate-800' : 'bg-white'}`}>
            <h3 className={`font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('writeDO').replace('DO1/DO2', outputPopup.coil_num === 1 ? 'DO1' : 'DO2')}</h3>
            <p className={`text-sm mb-3 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{t('confirmStopStart')}</p>
            <input type="password" placeholder={t('password')} value={password} onChange={(e) => setPassword(e.target.value)} className={`w-full px-3 py-2 rounded-lg border mb-2 ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-300'}`} />
            <label className="flex items-center gap-2 mb-3">
              <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
              <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>{t('confirmCheck')}</span>
            </label>
            <div className="flex gap-2">
              <button onClick={() => setOutputPopup(null)} className={`flex-1 py-2 rounded-lg ${isDark ? 'bg-slate-600 text-white' : 'bg-gray-200 text-gray-700'}`}>{t('cancel')}</button>
              <button onClick={handleWriteCoil} disabled={writing || !password || !confirmed} className="flex-1 py-2 rounded-lg bg-purple-600 text-white disabled:opacity-50">{writing ? t('sending') : t('send')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- Meter Details Accordion (dropdowns) ----------
function getMeterDetailsSections(useKw) {
  const p = useKw ? 'kW' : 'MW';
  const q = useKw ? 'kVAR' : 'MVAR';
  const e = useKw ? 'kWh' : 'MWh';
  const qh = useKw ? 'kVARh' : 'MVARh';
  const s = useKw ? 'kVA' : 'MVA';
  const sh = useKw ? 'kVAh' : 'MVAh';
  return [
    {
      id: 'power',
      titleKey: 'emSectionPower',
      items: [
        { key: 'active_power', label: `Real Power (${p})`, sub: 'Fuqia reale (aktive)', unit: 'MW' },
        { key: 'reactive_power', label: `Reactive Power (${q})`, sub: 'Fuqia reaktive', unit: 'MVAR' },
        { key: 'apparent_power', label: `Apparent Power (${s})`, sub: 'Fuqia e dukshme', unit: 'MVA' },
        { key: 'power_factor', label: 'Power Factor (PF)', sub: 'Faktori i fuqisë', unit: '' },
        { key: 'total_real_power', label: `Total Real Power (${p})`, sub: 'Fuqia totale e sistemit', unit: 'MW' },
      ],
    },
    {
      id: 'energy',
      titleKey: 'emSectionEnergy',
      items: [
        { key: 'total_export', label: `Energy Delivered (${e})`, sub: 'Energji e dërguar (eksport)', unit: 'MWh' },
        { key: 'total_import', label: `Energy Received (${e})`, sub: 'Energji e marrë (import)', unit: 'MWh' },
        { key: 'reactive_export', label: `Reactive Energy Delivered (${qh})`, sub: 'Energji reaktive e dërguar', unit: 'MVARh' },
        { key: 'apparent_energy', label: `Apparent Energy (${sh})`, sub: 'Energji totale e dukshme', unit: 'MVAh' },
      ],
    },
    {
      id: 'voltage',
      titleKey: 'emSectionVoltage',
      items: [
        { key: 'voltage_ab', label: 'Vab (L1-L2)', sub: 'Tensioni mes L1 dhe L2', unit: 'V' },
        { key: 'voltage_bc', label: 'Vbc (L2-L3)', sub: 'Tensioni mes L2 dhe L3', unit: 'V' },
        { key: 'voltage_ca', label: 'Vca (L3-L1)', sub: 'Tensioni mes L3 dhe L1', unit: 'V' },
        { key: 'vll_avg', label: 'VLL Average', sub: 'Mesatarja linjë-linjë', unit: 'V' },
        { key: 'voltage_a', label: 'Van (L1-N)', sub: 'Tensioni L1 ndaj N', unit: 'V' },
        { key: 'voltage_b', label: 'Vbn (L2-N)', sub: 'Tensioni L2 ndaj N', unit: 'V' },
        { key: 'voltage_c', label: 'Vcn (L3-N)', sub: 'Tensioni L3 ndaj N', unit: 'V' },
        { key: 'vln_avg', label: 'VLN Average', sub: 'Mesatarja linjë-neutral', unit: 'V' },
      ],
    },
    {
      id: 'current',
      titleKey: 'emSectionCurrent',
      items: [
        { key: 'current_a', label: 'Ia (L1 Current)', sub: 'Rryma faza L1', unit: 'A' },
        { key: 'current_b', label: 'Ib (L2 Current)', sub: 'Rryma faza L2', unit: 'A' },
        { key: 'current_c', label: 'Ic (L3 Current)', sub: 'Rryma faza L3', unit: 'A' },
        { key: 'current_avg', label: 'I Average', sub: 'Mesatarja e rrymës', unit: 'A' },
        { key: 'neutral_current', label: 'Neutral Current (In)', sub: 'Rryma në neutral', unit: 'A' },
        { key: 'ground_current', label: 'Ground Current (Ig)', sub: 'Rryma në tokëzim', unit: 'A' },
      ],
    },
    {
      id: 'frequency',
      titleKey: 'emSectionFrequency',
      items: [
        { key: 'frequency', label: 'Frequency (Hz)', sub: 'Frekuenca e rrjetit', unit: 'Hz' },
      ],
    },
    {
      id: 'demand',
      titleKey: 'emSectionDemand',
      items: [
        { key: 'peak_real_power', label: 'Peak Real Power (MW)', sub: 'Fuqia maksimale / Demand Real Present', unit: 'MW' },
        { key: 'peak_current', label: 'Peak / Demand Current (A)', sub: 'Rryma maksimale ose Demand Ia Present', unit: 'A' },
        { key: 'demand_power', label: 'Demand Power (MW)', sub: 'Demand Real Present – fuqia mesatare', unit: 'MW' },
        { key: 'peak_datetime', label: 'Date/Time at Peak', sub: 'Koha e peak-ut (nëse meter dërgon)', unit: '' },
        { key: 'energy_reset_datetime', label: 'Date/Time Last Reset', sub: 'Data/ora e resetit të energjisë', unit: '' },
      ],
    },
    {
      id: 'advanced',
      titleKey: 'emSectionAdvanced',
      items: [
        { key: 'min_values', label: 'Minimum Values', sub: 'Vlerat minimale', unit: '' },
        { key: 'max_values', label: 'Maximum Values', sub: 'Vlerat maksimale', unit: '' },
        { key: 'thd', label: 'Total Harmonic Distortion (THD)', sub: 'Shtrembërimi harmonik', unit: '%' },
      ],
    },
  ];
}

const VOLTAGE_DETAIL_KEYS = new Set([
  'voltage_ab',
  'voltage_bc',
  'voltage_ca',
  'vll_avg',
  'voltage_a',
  'voltage_b',
  'voltage_c',
  'vln_avg',
]);

function formatMeterValue(val, unit, useKw = false) {
  if (val == null || val === '' || val === '—') return '—';
  const n = Number(val);
  if (!Number.isFinite(n)) return String(val);
  if (!unit) return String(val);
  if (unit === 'V') return `${safeToFixed(n, 2)} V`;
  if (useKw) {
    if (unit === 'MW') return `${safeToFixed(Math.abs(n) * 1000, 2)} kW`;
    if (unit === 'MWh') return `${safeToFixed(n * 1000, 2)} kWh`;
    if (unit === 'MVAR') {
      if (Math.abs(n) >= 100) return `${safeToFixed(n, 2)} kVAR`;
      return `${safeToFixed(Math.abs(n) * 1000, 2)} kVAR`;
    }
    if (unit === 'MVA') return `${safeToFixed(Math.abs(n) * 1000, 2)} kVA`;
    if (unit === 'MVARh') {
      if (Math.abs(n) >= 100) return `${safeToFixed(n, 2)} kVARh`;
      return `${safeToFixed(n * 1000, 2)} kVARh`;
    }
    if (unit === 'MVAh') return `${safeToFixed(n * 1000, 2)} kVAh`;
  }
  if (unit === 'MW') return `${safeToFixed(Math.abs(n), 2)} MW`;
  if (unit === 'MWh') return `${safeToFixed(n, 2)} MWh`;
  const toMW = ['MVAR', 'MVA', 'MVARh', 'MVAh'];
  if (!useKw && toMW.includes(unit) && Math.abs(n) >= 100) return `${safeToFixed(n / 1000, 2)} ${unit}`;
  if (toMW.includes(unit)) return `${safeToFixed(n, 2)} ${unit}`;
  return `${val} ${unit}`;
}

function MeterDetailsAccordion({ readings, isDark, isWeb, useKw = false, t = (k) => k }) {
  const r = readings || {};
  const textPrimary = isDark ? 'text-white' : 'text-gray-900';
  const textSecondary = isDark ? 'text-gray-400' : 'text-gray-500';
  const borderCl = isDark ? 'border-slate-700' : 'border-gray-200';
  const sections = getMeterDetailsSections(useKw);

  return (
    <Accordion type="multiple" defaultValue={[]} className="w-full">
      {sections.map((sec) => (
        <AccordionItem key={sec.id} value={sec.id} className={`border-b ${borderCl}`}>
          <AccordionTrigger className={`${textPrimary} hover:no-underline py-3`}>
            {sec.titleKey ? t(sec.titleKey) : sec.title}
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-2">
              {sec.items.map((item) => {
                const val = r[item.key] ?? '—';
                const scaledVal =
                  VOLTAGE_DETAIL_KEYS.has(item.key) && Number.isFinite(Number(val))
                    ? Number(val) * 1000
                    : val;
                const display = item.unit ? formatMeterValue(scaledVal, item.unit, useKw) : (scaledVal ?? '—');
                return (
                  <div key={item.key} className="flex justify-between items-center py-1.5">
                    <span className={`text-sm ${textPrimary} pl-6`}>{item.label}</span>
                    <span className={`text-sm font-medium ${textPrimary} ${isWeb ? 'mr-[30%]' : ''}`}>{display}</span>
                  </div>
                );
              })}
            </div>
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}

function EmEnergyAnalysisLegend({ visible, onToggle, isDark, t, seriesList = EM_ENERGY_ANALYSIS_SERIES }) {
  const border = isDark ? 'border-slate-600' : 'border-gray-200';
  const label = isDark ? 'text-gray-200' : 'text-gray-800';
  return (
    <div className={`grid grid-cols-2 sm:grid-cols-3 gap-2 mt-3 pt-3 border-t ${border}`}>
      {seriesList.map((s) => (
        <label key={s.id} className={`flex items-center gap-2 cursor-pointer text-[11px] sm:text-xs ${label}`}>
          <input
            type="checkbox"
            checked={!!visible[s.id]}
            onChange={() => onToggle(s.id)}
            className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
          />
          <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: s.color }} />
          <span className="leading-tight">{t(s.labelKey)}</span>
        </label>
      ))}
    </div>
  );
}

function EmEnergyAnalysisSummaryBars({ graphPeriod, chartData, visible, t, isDark }) {
  if (graphPeriod !== 'Day') return null;
  const totals = computeEmEnergyAnalysisDayTotals(chartData);
  if (!totals) return null;
  const tp = isDark ? 'text-white' : 'text-gray-900';
  const ts = isDark ? 'text-gray-400' : 'text-gray-600';
  const showSystemBar = visible.systemProduction && totals.systemTotal > 1e-3;
  const showLoadBar = visible.loadConsumption && totals.loadTotal > 1e-3;
  if (!showSystemBar && !showLoadBar) return null;
  const ring = isDark ? 'ring-1 ring-slate-600' : 'ring-1 ring-gray-200';

  return (
    <div className="mt-4 space-y-5">
      {showSystemBar && (
        <div>
          <p className={`text-center text-sm font-medium ${tp}`}>
            {t('emSystemProductionTotal')}{' '}
            <span className="font-bold text-purple-500 dark:text-purple-400">{formatEmKwhSummary(totals.systemTotal)}</span>
          </p>
          <div className={`mt-2 flex h-9 w-full overflow-hidden rounded-lg ${ring}`}>
            <div
              className="flex min-w-0 items-center justify-center text-[10px] font-bold text-white"
              style={{
                width: `${Math.max(0, Math.min(100, totals.selfPctOfSystem))}%`,
                backgroundColor: '#14b8a6',
              }}
            >
              {visible.selfConsumption && totals.selfPctOfSystem >= 5 ? `${totals.selfPctOfSystem.toFixed(1)}%` : ''}
            </div>
            <div
              className="flex min-w-0 items-center justify-center text-[10px] font-bold text-white"
              style={{
                width: `${Math.max(0, Math.min(100, totals.exportPctOfSystem))}%`,
                backgroundColor: '#22c55e',
              }}
            >
              {visible.exportToGrid && totals.exportPctOfSystem >= 5 ? `${totals.exportPctOfSystem.toFixed(1)}%` : ''}
            </div>
          </div>
          <div className="mt-2 flex justify-between gap-3 text-xs">
            <div className={visible.selfConsumption ? 'min-w-0 flex-1' : 'hidden'}>
              <div className={ts}>{t('emSeriesSelfConsumption')}</div>
              <div className={`font-semibold ${tp}`}>{formatEmKwhSummary(totals.selfKwh)}</div>
            </div>
            <div className={`min-w-0 flex-1 text-right ${visible.exportToGrid ? '' : 'hidden'}`}>
              <div className={ts}>{t('emSeriesExportToGrid')}</div>
              <div className={`font-semibold ${tp}`}>{formatEmKwhSummary(totals.exportKwh)}</div>
            </div>
          </div>
        </div>
      )}
      {showLoadBar && (
        <div>
          <p className={`text-center text-sm font-medium ${tp}`}>
            {t('emLoadConsumptionTotal')}{' '}
            <span className="font-bold text-orange-500 dark:text-orange-400">{formatEmKwhSummary(totals.loadTotal)}</span>
          </p>
          <div className={`mt-2 flex h-9 w-full overflow-hidden rounded-lg ${ring}`}>
            <div
              className="flex min-w-0 items-center justify-center text-[10px] font-bold text-white"
              style={{
                width: `${Math.max(0, Math.min(100, totals.selfPctOfLoad))}%`,
                backgroundColor: '#eab308',
              }}
            >
              {visible.selfConsumption && totals.selfPctOfLoad >= 1 ? `${totals.selfPctOfLoad.toFixed(1)}%` : ''}
            </div>
            <div
              className="flex min-w-0 items-center justify-center text-[10px] font-bold text-white"
              style={{
                width: `${Math.max(0, Math.min(100, totals.importPctOfLoad))}%`,
                backgroundColor: '#a89078',
              }}
            >
              {visible.importFromGrid && totals.importPctOfLoad >= 5 ? `${totals.importPctOfLoad.toFixed(1)}%` : ''}
            </div>
          </div>
          <div className="mt-2 flex justify-between gap-3 text-xs">
            <div className={visible.selfConsumption ? 'min-w-0 flex-1' : 'hidden'}>
              <div className={ts}>{t('emSeriesSelfConsumption')}</div>
              <div className={`font-semibold ${tp}`}>{formatEmKwhSummary(totals.selfKwh)}</div>
            </div>
            <div className={`min-w-0 flex-1 text-right ${visible.importFromGrid ? '' : 'hidden'}`}>
              <div className={ts}>{t('emSeriesImportFromGrid')}</div>
              <div className={`font-semibold ${tp}`}>{formatEmKwhSummary(totals.importKwh)}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** Tooltip që nuk përfshin seritë e fshuara — shmang lag nga rifiltrimi i të gjitha linjave. */
function EmDayChartTooltip({ active, payload, label, visible, displayUnit, t, isDark, seriesList = EM_ENERGY_ANALYSIS_SERIES }) {
  if (!active || !payload?.length) return null;
  const filtered = payload.filter((item) => {
    const s = seriesList.find((x) => x.dataKey === item.dataKey);
    return s && visible[s.id];
  });
  if (!filtered.length) return null;
  const border = isDark ? '#334155' : '#e5e7eb';
  return (
    <div
      className="rounded-lg border px-3 py-2 text-xs shadow-md"
      style={{
        backgroundColor: isDark ? '#1e293b' : '#fff',
        border: `1px solid ${border}`,
        color: isDark ? '#fff' : '#000',
      }}
    >
      {label ? <p className="mb-1.5 font-medium opacity-90">{`${t('emChartTimeLabel')}: ${label}`}</p> : null}
      <ul className="space-y-0.5">
        {filtered.map((item) => {
          const soloExport = filtered.length === 1 && item.dataKey === 'exportToGrid';
          return (
            <li
              key={String(item.dataKey)}
              className={soloExport ? 'font-medium tabular-nums text-right' : 'flex justify-between gap-4'}
            >
              {!soloExport ? <span style={{ color: item.color }}>{item.name}</span> : null}
              <span className={soloExport ? '' : 'font-medium tabular-nums'}>
                {chartPowerTickLabel(Number(item.value), displayUnit)}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function EmRealtimeEnergyDayChartInner({ chartData, visible, isDark, t, displayUnit, seriesList = EM_ENERGY_ANALYSIS_SERIES }) {
  const visibleKey = useMemo(
    () => seriesList.map((s) => (visible[s.id] ? '1' : '0')).join(''),
    [visible, seriesList],
  );

  const maxY = useMemo(() => {
    let m = 1;
    for (const row of chartData || []) {
      for (const s of seriesList) {
        if (!visible[s.id]) continue;
        const v = Number(row[s.dataKey]);
        if (Number.isFinite(v) && v > m) m = v;
      }
    }
    return m * 1.08;
  }, [chartData, visibleKey, seriesList]);

  return (
    <AreaChart data={chartData || []} margin={{ top: 14, right: 12, left: 8, bottom: 8 }}>
      <defs>
        {seriesList.map((s) => (
          <linearGradient key={s.id} id={`emg-${s.id}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={s.color} stopOpacity={0.35} />
            <stop offset="100%" stopColor={s.color} stopOpacity={0} />
          </linearGradient>
        ))}
      </defs>
      <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#334155' : '#e5e7eb'} vertical={false} />
      <XAxis
        dataKey="name"
        axisLine={false}
        tickLine={false}
        tick={{ fill: isDark ? '#9ca3af' : '#6b7280', fontSize: 10 }}
        interval={0}
        ticks={['00:00', '04:00', '08:00', '12:00', '16:00', '20:00', '23:00']}
      />
      <YAxis
        domain={[0, maxY]}
        axisLine={false}
        tickLine={false}
        width={52}
        tick={{ fill: isDark ? '#9ca3af' : '#6b7280', fontSize: 10 }}
        tickFormatter={(v) => chartPowerTickLabel(v, displayUnit)}
      />
      <Tooltip
        content={(props) => (
          <EmDayChartTooltip {...props} visible={visible} displayUnit={displayUnit} t={t} isDark={isDark} seriesList={seriesList} />
        )}
      />
      {seriesList.map((s) => {
        const on = !!visible[s.id];
        return (
          <Area
            key={s.id}
            type="linear"
            dataKey={s.dataKey}
            name={t(s.labelKey)}
            stroke={s.color}
            strokeOpacity={on ? 1 : 0}
            strokeWidth={on ? 2 : 0}
            fill={on ? `url(#emg-${s.id})` : 'none'}
            fillOpacity={on ? 1 : 0}
            isAnimationActive={false}
            dot={false}
            activeDot={on}
          />
        );
      })}
    </AreaChart>
  );
}

const EmRealtimeEnergyDayChart = memo(EmRealtimeEnergyDayChartInner, (prev, next) => {
  if (prev.chartData !== next.chartData || prev.isDark !== next.isDark || prev.displayUnit !== next.displayUnit) return false;
  if (prev.t !== next.t) return false;
  if (prev.seriesList !== next.seriesList) return false;
  for (const s of next.seriesList) {
    if (!!prev.visible[s.id] !== !!next.visible[s.id]) return false;
  }
  return true;
});

// ============================================
// MOBILE VIEW COMPONENT
// ============================================
const MobileEnergyMeterView = ({ meter, dataLogger, companies, navigate, isDark, readings, lastUpdated, readingsList = [], isStale, activePowerMW, todayEnergy, monthlyEnergy, totalEnergy, chartData, chartLoading, graphPeriod, setGraphPeriod, selectedDate, setSelectedDate, alarms = [], alarmHistory = [], alarmsLoading = false, mainTab, setMainTab, fetchData, rawPayload, powerFlowKw, useKw = false, emAnalysisSeriesVisible = defaultEmAnalysisSeriesVisible(), emAnalysisSeriesVisibleDeferred, onToggleEmAnalysisSeries = () => { } }) => {
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const { user } = useAuth();
  const { t } = useLanguage();
  const isSuperAdmin = user?.role === 'SUPERADMIN';

  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const clickCountRef = useRef(0);
  const clickTimerRef = useRef(null);

  const currentCompany = companies?.find(c => c.id === (dataLogger?.company_id || meter?.company_id));
  const emEnergyAnalysisHib = isHibEnergyFlowCompany(currentCompany);
  const emAnalysisSeriesList = useMemo(() => getEmEnergyAnalysisSeriesForMeter(), []);
  const cardShadowClass = "shadow-[6px_6px_12px_rgba(0,0,0,0.08),-6px_-6px_12px_rgba(255,255,255,0.9)] dark:shadow-none";
  const safeReadings = readings || buildReadingsFromPayload(null, meter);
  const emLabels = powerEnergyLabels(meter?.display_power_unit);

  const datePickerRef = useRef(null);
  const monthPickerRef = useRef(null);

  const changeDate = (delta) => {
    setSelectedDate((prev) => {
      const base = prev || todayStr();
      const d = new Date(`${base}T12:00:00`);
      if (Number.isNaN(d.getTime())) return todayStr();
      if (graphPeriod === 'Day') d.setDate(d.getDate() + delta);
      else if (graphPeriod === 'Month') d.setMonth(d.getMonth() + delta);
      else if (graphPeriod === 'Year') d.setFullYear(d.getFullYear() + delta);
      return clampEmChartDate(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`, graphPeriod);
    });
  };

  const graphDateDisplay =
    graphPeriod === 'Day'
      ? selectedDate
      : graphPeriod === 'Month'
        ? (selectedDate ? selectedDate.slice(0, 7) : '')
        : graphPeriod === 'Year'
          ? (selectedDate ? selectedDate.slice(0, 4) : '')
          : '';

  const openPeriodPicker = () => {
    if (graphPeriod === 'Day') {
      datePickerRef.current?.showPicker?.();
      datePickerRef.current?.click();
    } else if (graphPeriod === 'Month') {
      monthPickerRef.current?.showPicker?.();
      monthPickerRef.current?.click();
    }
  };

  const handlePhotoClick = () => {
    if (!isSuperAdmin) return;
    clickCountRef.current += 1;
    if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
    if (clickCountRef.current >= 3) {
      clickCountRef.current = 0;
      setShowPhotoModal(true);
    } else {
      clickTimerRef.current = setTimeout(() => { clickCountRef.current = 0; }, 500);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const token = localStorage.getItem('access_token');
      await axios.post(`${BACKEND_URL}/api/upload/photo/energy-meter/${meter.id}`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      setShowPhotoModal(false);
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('photoUploadError'));
    } finally {
      setUploading(false);
    }
  };

  return (
    <Layout>
      <div className="h-full flex flex-col bg-[#f5f5f5] dark:bg-gradient-to-b dark:from-[#0f172a] dark:to-black">
        {/* Header with Company Dropdown – si Data Logger */}
        <div className="p-4 border-b border-gray-200 dark:border-slate-700 bg-white dark:bg-[#0f172a]">
          <div className="flex items-center justify-between mb-3">
            <div className="relative">
              <button
                onClick={() => setShowCompanyDropdown(!showCompanyDropdown)}
                className="flex items-center gap-2"
              >
                <span className="text-lg font-bold text-gray-900 dark:text-white">{currentCompany?.name || t('noCompanies')}</span>
                <svg className={`w-4 h-4 text-gray-500 dark:text-gray-400 transition-transform ${showCompanyDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {showCompanyDropdown && companies && companies.length > 0 && (
                <div className="absolute top-full left-0 mt-2 w-56 bg-white dark:bg-[#1e293b] rounded-xl shadow-lg z-50 overflow-hidden border border-gray-200 dark:border-transparent">
                  {companies.map(c => (
                    <button
                      key={c.id}
                      onClick={() => {
                        setShowCompanyDropdown(false);
                        navigate(`/devices?company=${c.id}`);
                      }}
                      className={`w-full text-left px-4 py-3 hover:bg-gray-100 dark:hover:bg-[#2d3a4f] transition border-b border-gray-100 dark:border-[#2d3a4f] last:border-0 ${currentCompany?.id === c.id
                        ? 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 font-medium'
                        : 'text-gray-700 dark:text-white'
                        }`}
                    >
                      {c.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              onClick={() => currentCompany ? navigate(`/devices?company=${currentCompany.id}`) : navigate('/devices')}
              className="w-10 h-10 bg-[#e8e8e8] dark:bg-slate-700 rounded-full flex items-center justify-center hover:bg-gray-300 dark:hover:bg-slate-600 transition"
            >
              <svg className="w-5 h-5 text-gray-600 dark:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="flex gap-2 flex-1 min-w-0">
            <button
              onClick={() => currentCompany ? navigate(`/devices?company=${currentCompany.id}`) : navigate('/devices')}
              className="px-3 py-2 rounded-full text-sm font-medium transition shrink-0 bg-[#e8e8e8] dark:bg-slate-700 text-gray-600 dark:text-gray-300"
            >
              {t('allDevices')}
            </button>
            <button
              onClick={() => currentCompany ? navigate(`/devices?company=${currentCompany.id}&tab=inverter`) : navigate('/devices?tab=inverter')}
              className="px-3 py-2 rounded-full text-sm font-medium transition shrink-0 bg-[#e8e8e8] dark:bg-slate-700 text-gray-600 dark:text-gray-300"
            >
              {t('inverterTab')}
            </button>
            <button
              onClick={() => currentCompany ? navigate(`/devices?company=${currentCompany.id}&tab=ems`) : navigate('/devices?tab=ems')}
              className="px-3 py-2 rounded-full text-sm font-medium transition shrink-0 bg-blue-600 text-white"
            >
              {t('emsTab')}
            </button>
          </div>
        </div>

        {/* Section Title – si Data Logger */}
        <div className="px-4 py-3 bg-[#f0f0f0] dark:bg-[#0f172a]/80">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('energyMeter')}</h3>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-5 pb-4">
          {/* Device Card – si Data Logger: badge, foto, emri poshtë */}
          {/*<div className={`bg-white dark:bg-[#1e293b] rounded-xl p-4 mt-3 ${cardShadowClass}`}>
            <div className="flex items-start justify-between mb-3">
              <span className={`px-3 py-1 text-xs font-bold rounded ${isStale ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400' : 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'}`}>
                {isStale ? t('offline') : t('online')}
              </span>
            </div>
            <div
              className="h-28 flex items-center justify-center mb-3 cursor-pointer"
              onClick={handlePhotoClick}
            >
              {meter?.photo_url ? (
                <img src={`${BACKEND_URL}${meter.photo_url}`} alt={meter.name} className="max-h-full max-w-full object-contain" />
              ) : (
                <svg className="w-20 h-20 text-gray-400 dark:text-gray-500" viewBox="0 0 100 100" fill="currentColor">
                  <rect x="20" y="25" width="15" height="50" rx="2" />
                  <rect x="42" y="15" width="15" height="60" rx="2" />
                  <rect x="64" y="35" width="15" height="40" rx="2" />
                </svg>
              )}
            </div>
            <div className="text-center">
              <p className="text-gray-600 dark:text-gray-400 text-sm">{meter?.name || t('energyMeter')} ›</p>
              {lastUpdated && <p className="text-gray-400 dark:text-gray-500 text-xs">{t('updatedLabel')} {lastUpdated}</p>}
            </div>
          </div>*/}

          {!isHibEnergyFlowCompany(currentCompany) && (
            <div className="em-mobile-flow-parity w-full">
              <EnergyMeterSolidSolarFlow
                className="mt-3"
                displayPowerUnit={meter?.display_power_unit}
                powerFlowKw={powerFlowKw}
                activePowerMW={activePowerMW}
                useKw={useKw}
                isDark={isDark}
              />
            </div>
          )}
          {isHibEnergyFlowCompany(currentCompany) && (
            <EnergyPowerFlowDiagram
              className="mt-3"
              isDark={isDark}
              displayPowerUnit={meter?.display_power_unit}
              {...powerFlowKw}
            />
          )}

          {/* Main Tabs: Details | Real Time | Alarms | IN-OUT */}
          <div className="flex border-b border-gray-200 dark:border-slate-700 mt-4 gap-1">
            <button
              onClick={() => setMainTab('details')}
              className={`px-3 py-3 text-sm font-medium ${mainTab === 'details' ? 'border-b-2 border-purple-500 text-purple-600 dark:text-purple-400' : 'text-gray-500 dark:text-gray-400'}`}
            >
              {t('details')}
            </button>
            <button
              onClick={() => setMainTab('realtime')}
              className={`px-3 py-3 text-sm font-medium ${mainTab === 'realtime' ? 'border-b-2 border-purple-500 text-purple-600 dark:text-purple-400' : 'text-gray-500 dark:text-gray-400'}`}
            >
              {t('realTime')}
            </button>
            <button
              onClick={() => setMainTab('alarms')}
              className={`px-3 py-3 text-sm font-medium flex items-center gap-1 ${mainTab === 'alarms' ? 'border-b-2 border-purple-500 text-purple-600 dark:text-purple-400' : 'text-gray-500 dark:text-gray-400'}`}
            >
              {t('alarms')} <span className={`text-xs px-1.5 py-0.5 rounded ${(getDisplayAlarms(alarms).filter((a) => String(a?.state).toUpperCase() === 'ON').length || 0) > 0 ? 'bg-red-500/80 text-white' : 'bg-gray-300 dark:bg-slate-600 text-gray-600 dark:text-gray-400'}`}>{getDisplayAlarms(alarms).filter((a) => String(a?.state).toUpperCase() === 'ON').length ?? 0}</span>
            </button>
            <button
              onClick={() => setMainTab('inout')}
              className={`px-3 py-3 text-sm font-medium ${mainTab === 'inout' ? 'border-b-2 border-purple-500 text-purple-600 dark:text-purple-400' : 'text-gray-500 dark:text-gray-400'}`}
            >
              {t('inOut')}
            </button>
          </div>

          {mainTab === 'alarms' ? (
            /* Alarms – API vetëm kur hapet skeda */
            <div className={`mt-4 space-y-2 ${cardShadowClass}`}>
              {alarmsLoading ? (
                <div className={`rounded-xl py-10 text-center text-sm ${isDark ? 'bg-[#1e293b] text-gray-400 border border-slate-700' : 'bg-white text-gray-500 border border-gray-200'}`}>{t('loading')}</div>
              ) : (
              <>
              <div className={`rounded-xl overflow-hidden ${isDark ? 'bg-[#1e293b] border border-slate-700' : 'bg-white border border-gray-200'}`}>
                <div className="px-4 py-3 border-b border-gray-200 dark:border-slate-600">
                  <h3 className="font-semibold text-gray-900 dark:text-white">{t('alarms')} ({getDisplayAlarms(alarms).filter((a) => String(a?.state).toUpperCase() === 'ON').length}/{getDisplayAlarms(alarms).length})</h3>
                </div>
                <div className={`px-4 pb-3 max-h-40 overflow-y-auto ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                  {getDisplayAlarms(alarms).map((a, i) => {
                    const state = (a?.state ?? 'OFF').toUpperCase();
                    const isOn = state === 'ON';
                    return (
                      <div key={a?.no ?? i} className={`py-2.5 border-b border-gray-200 dark:border-slate-600 last:border-0 ${isOn ? 'bg-red-50/50 dark:bg-red-900/10' : ''}`}>
                        <span className={`text-sm ${isOn ? 'text-red-700 dark:text-red-300 font-medium' : 'text-gray-900 dark:text-white'}`}>{formatAlarmDisplay(a)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
              {alarmHistory.length > 0 && (
                <div className={`rounded-xl overflow-hidden ${isDark ? 'bg-[#1e293b] border border-slate-700' : 'bg-white border border-gray-200'}`}>
                  <div className="px-4 py-2 border-b border-gray-200 dark:border-slate-600">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{t('alarmHistory') || 'Histori alarmeve'}</h3>
                  </div>
                  <div className="max-h-48 overflow-y-auto">
                    <table className="w-full text-[11px]">
                      <thead className={isDark ? 'bg-slate-800/50 text-gray-400' : 'bg-gray-50 text-gray-600'}>
                        <tr><th className="px-2 py-1.5 text-left font-medium">Data/ora</th><th className="px-2 py-1.5 text-left font-medium">Status</th></tr>
                      </thead>
                      <tbody className={isDark ? 'text-gray-300' : 'text-gray-700'}>
                        {alarmHistory.slice(0, 30).map((h, i) => (
                          <tr key={i} className={i % 2 === 0 ? (isDark ? 'bg-[#1e293b]' : 'bg-white') : (isDark ? 'bg-slate-800/30' : 'bg-gray-50/50')}>
                            <td className="px-2 py-1 whitespace-nowrap">{h.ts ? new Date(h.ts).toLocaleString('sq-AL', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                            <td className={`px-2 py-1 font-medium ${h.active ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}`}>{h.active ? (t('alarmActive') || 'Aktiv') : (t('alarmInactive') || 'Jo aktiv')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              </>
              )}
            </div>
          ) : mainTab === 'inout' ? (
            <DigitalIOSection meter={meter} readings={readings} rawPayload={rawPayload} isDark={isDark} isMobile fetchData={fetchData} t={t} />
          ) : mainTab === 'details' ? (
            <>
              {/* 4 Main Cards */}
              <div className="grid grid-cols-2 gap-3 mt-4">
                <div className={`bg-white dark:bg-[#1e293b] rounded-xl p-4 ${cardShadowClass}`}>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('activePowerReal')}</p>
                  <p className="text-xl font-bold text-green-500">{safeToFixed(useKw ? (typeof activePowerMW === 'number' ? Math.abs(activePowerMW) : Math.abs(Number(activePowerMW || 0))) * 1000 : (typeof activePowerMW === 'number' ? Math.abs(activePowerMW) : Math.abs(Number(activePowerMW || 0))), 2)} <span className="text-sm font-normal">{emLabels.power}</span></p>
                </div>
                <div className={`bg-white dark:bg-[#1e293b] rounded-xl p-4 ${cardShadowClass}`}>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('todayEnergy')}</p>
                  <p className="text-xl font-bold text-green-500">{todayEnergy != null ? safeToFixed(useKw ? Number(todayEnergy) * 1000 : Number(todayEnergy), useKw ? 1 : 3) : '—'} <span className="text-sm font-normal">{emLabels.energy}</span></p>
                </div>
                <div className={`bg-white dark:bg-[#1e293b] rounded-xl p-4 ${cardShadowClass}`}>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('monthlyEnergy')}</p>
                  <p className="text-xl font-bold text-green-500">{monthlyEnergy != null ? safeToFixed(useKw ? Number(monthlyEnergy) * 1000 : Number(monthlyEnergy), useKw ? 1 : 3) : '—'} <span className="text-sm font-normal">{emLabels.energy}</span></p>
                </div>
                <div className={`bg-white dark:bg-[#1e293b] rounded-xl p-4 ${cardShadowClass}`}>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('totalEnergyLabel')}</p>
                  <p className="text-xl font-bold text-green-500">{totalEnergy != null ? safeToFixed(useKw ? Number(totalEnergy) * 1000 : Number(totalEnergy), useKw ? 1 : 3) : '—'} <span className="text-sm font-normal">{emLabels.energy}</span></p>
                </div>
              </div>

              {/* Meter Details - Accordion dropdowns */}
              <div className={`mt-4 bg-white dark:bg-[#1e293b] rounded-xl p-4 ${cardShadowClass}`}>
                <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-3 text-center">{t('meterDetails')}</h3>
                <MeterDetailsAccordion readings={safeReadings} isDark={isDark} useKw={useKw} t={t} />
              </div>
            </>
          ) : (
            /* Real Time - graph on mobile */
            <>
              <div className="flex justify-center gap-1.5 px-4 mt-4">
                {['Day', 'Month', 'Year', 'Total'].map((p) => (
                  <button key={p} onClick={() => setGraphPeriod?.(p)} className={`px-4 py-2 rounded-lg text-xs font-medium ${graphPeriod === p ? 'bg-purple-600 text-white' : 'bg-gray-200 dark:bg-slate-700 text-gray-600 dark:text-gray-300'}`}>
                    {p}
                  </button>
                ))}
              </div>
              {(graphPeriod === 'Day' || graphPeriod === 'Month' || graphPeriod === 'Year') && (
                <div className="flex items-center justify-center gap-2 px-4 mt-3 flex-wrap">
                  <input
                    ref={datePickerRef}
                    type="date"
                    max={todayStr()}
                    value={selectedDate || todayStr()}
                    onChange={(e) => e.target.value && setSelectedDate(e.target.value)}
                    className="sr-only"
                    tabIndex={-1}
                    aria-hidden
                  />
                  <input
                    ref={monthPickerRef}
                    type="month"
                    max={todayStr().slice(0, 7)}
                    value={(selectedDate || todayStr()).slice(0, 7)}
                    onChange={(e) => e.target.value && setSelectedDate(`${e.target.value}-01`)}
                    className="sr-only"
                    tabIndex={-1}
                    aria-hidden
                  />
                  <button type="button" onClick={() => changeDate(-1)} className="text-gray-500 dark:text-gray-400 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700" aria-label="Previous">
                    ◀
                  </button>
                  {graphPeriod === 'Year' ? (
                    <select
                      value={(selectedDate || todayStr()).slice(0, 4)}
                      onChange={(e) => setSelectedDate(`${e.target.value}-01-01`)}
                      className="text-sm text-gray-800 dark:text-gray-100 bg-gray-100 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-lg px-2 py-1.5 min-w-[5rem]"
                    >
                      {Array.from({ length: Math.max(1, new Date().getFullYear() - 2019) }, (_, i) => 2020 + i)
                        .filter((y) => y <= new Date().getFullYear())
                        .map((y) => (
                          <option key={y} value={y}>{y}</option>
                        ))}
                    </select>
                  ) : (
                    <button
                      type="button"
                      onClick={openPeriodPicker}
                      className="text-sm font-medium text-gray-800 dark:text-gray-100 min-w-[6.5rem] text-center px-2 py-1 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700"
                    >
                      {graphDateDisplay}
                    </button>
                  )}
                  <button type="button" onClick={() => changeDate(1)} className="text-gray-500 dark:text-gray-400 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700" aria-label="Next">
                    ▶
                  </button>
                </div>
              )}
              <div className={`mt-4 bg-white dark:bg-[#1e293b] rounded-xl p-4 ${cardShadowClass}`}>
                <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-3">{t('energyAnalysisLabel')}</h3>
                <div className="h-44 min-h-[180px] relative">
                  {chartLoading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-white/80 dark:bg-[#0f172a]/80 z-10 rounded-lg">
                      <div className="flex flex-col items-center gap-2">
                        <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
                        <span className="text-sm text-gray-600 dark:text-gray-400">{t('loading')}</span>
                      </div>
                    </div>
                  )}
                  <ResponsiveContainer width="100%" height="100%">
                    {graphPeriod === 'Day' ? (
                      <EmRealtimeEnergyDayChart
                        chartData={chartData}
                        visible={
                          emEnergyAnalysisHib
                            ? emAnalysisSeriesVisibleDeferred ?? emAnalysisSeriesVisible
                            : EM_ANALYSIS_VISIBLE_EXPORT_ONLY
                        }
                        isDark={isDark}
                        t={t}
                        displayUnit={meter?.display_power_unit}
                        seriesList={emAnalysisSeriesList}
                      />
                    ) : (
                      <BarChart data={chartData || []} margin={{ top: 14, right: 12, left: 8, bottom: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#334155' : '#e5e7eb'} vertical={false} />
                        <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: isDark ? '#9ca3af' : '#6b7280', fontSize: 10 }} interval="preserveStartEnd" />
                        <YAxis axisLine={false} tickLine={false} width={58} padding={{ top: 10, bottom: 8 }} tick={{ fill: isDark ? '#9ca3af' : '#6b7280', fontSize: 10 }} tickFormatter={(v) => chartEnergyDeltaTickLabel(v, meter?.display_power_unit)} />
                        <Tooltip contentStyle={{ backgroundColor: isDark ? '#1e293b' : '#fff', border: `1px solid ${isDark ? '#334155' : '#e5e7eb'}`, borderRadius: '8px', color: isDark ? '#fff' : '#000' }} formatter={(v) => [chartEnergyDeltaTickLabel(v, meter?.display_power_unit), 'Export']} />
                        <Bar dataKey="value" fill="#a855f7" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    )}
                  </ResponsiveContainer>
                </div>
                {graphPeriod === 'Day' && emEnergyAnalysisHib && (
                  <>
                    <EmEnergyAnalysisLegend
                      visible={emAnalysisSeriesVisible}
                      onToggle={onToggleEmAnalysisSeries}
                      isDark={isDark}
                      t={t}
                      seriesList={emAnalysisSeriesList}
                    />
                    <EmEnergyAnalysisSummaryBars
                      graphPeriod={graphPeriod}
                      chartData={chartData}
                      visible={emAnalysisSeriesVisibleDeferred ?? emAnalysisSeriesVisible}
                      t={t}
                      isDark={isDark}
                    />
                  </>
                )}
                <div className="flex justify-end items-center mt-3 pt-3 border-t border-gray-200 dark:border-slate-600">
                  <button type="button" onClick={() => {
                    const header = graphPeriod === 'Day' ? 'Time,Avg kW (5min),Delta GWh' : 'Period,Export GWh';
                    const rows = graphPeriod === 'Day'
                      ? (chartData || []).map((x) => `${x.name},${Number(x.value).toFixed(4)},${x.valueMwh != null ? (Number(x.valueMwh) / 1000).toFixed(6) : ''}`)
                      : (chartData || []).map((x) => `${x.name},${(Number(x.value) / 1000).toFixed(6)}`);
                    const b = new Blob([`${header}\n${rows.join('\n')}`], { type: 'text/csv' });
                    const u = URL.createObjectURL(b);
                    const a = document.createElement('a');
                    a.href = u;
                    a.download = `energy-export-${graphPeriod || 'Day'}-${Date.now()}.csv`;
                    a.click();
                    URL.revokeObjectURL(u);
                  }} className="p-1.5 rounded text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-700" title={t('exportLabel')}><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg></button>
                </div>
              </div>
            </>
          )}
          <div className="h-4" />
        </div>

        {/* Photo Upload Modal - mobile: në mes, X */}
        {showPhotoModal && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-slate-800 rounded-2xl p-4 md:p-6 w-full max-w-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base md:text-lg font-bold text-gray-900 dark:text-white">{t('photoUpload')}</h3>
                <button type="button" onClick={() => setShowPhotoModal(false)} className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-gray-400" aria-label={t('close')}>✕</button>
              </div>
              <input ref={fileInputRef} type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" />
              <div className="flex gap-3">
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50"
                >
                  {uploading ? t('loading') : t('selectPhoto')}
                </button>
                <button
                  onClick={() => setShowPhotoModal(false)}
                  className="px-4 py-2 bg-gray-200 dark:bg-slate-700 text-gray-700 dark:text-gray-300 rounded-lg"
                >
                  {t('cancel')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

// ============================================
// WEB VIEW COMPONENT (similar to InverterDetail)
// ============================================
const WebEnergyMeterView = ({ meter, dataLogger, companies, navigate, isDark, readings, lastUpdated, readingsList = [], isStale, activePowerMW, todayEnergy, monthlyEnergy, totalEnergy, chartData, chartLoading, graphPeriod, setGraphPeriod, selectedDate, setSelectedDate, alarms = [], alarmHistory = [], alarmsLoading = false, mainTab, setMainTab, fetchData, rawPayload, powerFlowKw, useKw = false, emAnalysisSeriesVisible = defaultEmAnalysisSeriesVisible(), emAnalysisSeriesVisibleDeferred, onToggleEmAnalysisSeries = () => { } }) => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const isSuperAdmin = user?.role === 'SUPERADMIN';

  const fileInputRef = useRef(null);
  const datePickerRef = useRef(null);
  const monthPickerRef = useRef(null);
  const clickCountRef = useRef(0);
  const clickTimerRef = useRef(null);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [uploading, setUploading] = useState(false);

  const currentCompany = companies?.find(c => c.id === (dataLogger?.company_id || meter?.company_id));
  const emEnergyAnalysisHib = isHibEnergyFlowCompany(currentCompany);
  const emAnalysisSeriesList = useMemo(() => getEmEnergyAnalysisSeriesForMeter(), []);
  const safeReadings = readings || buildReadingsFromPayload(null, meter);
  const emLabels = powerEnergyLabels(meter?.display_power_unit);

  const changeDate = (delta) => {
    setSelectedDate?.((prev) => {
      const base = prev || todayStr();
      const d = new Date(`${base}T12:00:00`);
      if (Number.isNaN(d.getTime())) return todayStr();
      if (graphPeriod === 'Day') d.setDate(d.getDate() + delta);
      else if (graphPeriod === 'Month') d.setMonth(d.getMonth() + delta);
      else if (graphPeriod === 'Year') d.setFullYear(d.getFullYear() + delta);
      return clampEmChartDate(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`, graphPeriod);
    });
  };

  const graphDateDisplay = graphPeriod === 'Day' ? selectedDate : graphPeriod === 'Month' ? (selectedDate ? `${selectedDate.slice(0, 7)}` : '') : graphPeriod === 'Year' ? (selectedDate ? selectedDate.slice(0, 4) : '') : 'Total';

  const openWebPeriodPicker = () => {
    if (graphPeriod === 'Day') {
      datePickerRef.current?.showPicker?.();
      datePickerRef.current?.click();
    } else if (graphPeriod === 'Month') {
      monthPickerRef.current?.showPicker?.();
      monthPickerRef.current?.click();
    }
  };

  const handlePhotoClick = () => {
    if (!isSuperAdmin) return;
    clickCountRef.current += 1;
    if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
    if (clickCountRef.current >= 3) {
      clickCountRef.current = 0;
      setShowPhotoModal(true);
    } else {
      clickTimerRef.current = setTimeout(() => { clickCountRef.current = 0; }, 500);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const token = localStorage.getItem('access_token');
      await axios.post(`${BACKEND_URL}/api/upload/photo/energy-meter/${meter.id}`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      setShowPhotoModal(false);
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('photoUploadError'));
    } finally {
      setUploading(false);
    }
  };

  // Theme colors
  const bgMain = isDark ? '#0f172a' : '#f8fafc';
  const bgCard = isDark ? '#1e293b' : '#ffffff';
  const textPrimary = isDark ? 'text-white' : 'text-gray-900';
  const textSecondary = isDark ? 'text-gray-400' : 'text-gray-500';
  const borderColor = isDark ? 'border-[#334155]' : 'border-gray-200';

  return (
    <Layout>
      <div className="flex h-full min-h-full flex-col overflow-hidden" style={{ backgroundColor: bgMain }}>
        {/* Header */}
        <div className={`flex-shrink-0 border-b ${borderColor} px-4 py-3`} style={{ backgroundColor: bgCard }}>
          <div className="flex items-center gap-3">
            <button
              onClick={() => currentCompany ? navigate(`/devices?company=${currentCompany.id}`) : navigate('/devices')}
              className={`${textSecondary} hover:opacity-80`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <span className={`font-semibold ${textPrimary}`}>{meter?.name || 'Energy Meter'}</span>
            <div className="flex-1"></div>
            {lastUpdated && <span className={`text-sm ${textSecondary}`}>Updated {lastUpdated}</span>}
          </div>
        </div>

        {/* Main Content — zgjatet deri në fund të faqes; kolonat items-stretch */}
        <div className="flex min-h-0 flex-1 items-stretch overflow-hidden">
          {/* Left Panel - Device Info (stil i njëjtë si Inverter: rounded card, pa vijë vertikale) */}
          <div className={`flex h-full min-h-0 w-[22rem] shrink-0 flex-col overflow-y-auto px-5 py-4`} style={{ backgroundColor: bgMain }}>
            <div
              className="flex min-h-full w-full flex-col rounded-xl border shadow-sm"
              style={{ backgroundColor: bgCard, borderColor: isDark ? '#334155' : '#f3f4f6' }}
            >
              {/* Rrjedhje energjie — sipër fotos (e njëjta si në mobile: HIB → 3D, jo-HIB → Solar/GRID) */}
              <div className={`shrink-0 border-b px-3 pb-2 pt-2 ${borderColor}`}>
                {isHibEnergyFlowCompany(currentCompany) ? (
                  <div className="ef3d-em-sidebar-wrap w-full max-w-full overflow-hidden">
                    <EnergyPowerFlowDiagram
                      className="w-full"
                      isDark={isDark}
                      displayPowerUnit={meter?.display_power_unit}
                      {...powerFlowKw}
                    />
                  </div>
                ) : (
                  <EnergyMeterSolidSolarFlow
                    compactMobile={false}
                    className="w-full"
                    displayPowerUnit={meter?.display_power_unit}
                    powerFlowKw={powerFlowKw}
                    activePowerMW={activePowerMW}
                    useKw={useKw}
                    isDark={isDark}
                  />
                )}
              </div>

              {/* Device Photo — kompakt që të mbetet hapësirë për diagramën e rrjedhës */}
              <div className={`px-4 pt-3 pb-2 border-b ${borderColor}`} onClick={handlePhotoClick}>
                <div className={`w-full h-28 max-h-28 rounded-lg flex items-center justify-center overflow-hidden cursor-pointer ${isDark ? 'bg-[#1e293b]' : 'bg-white'}`}>
                  {meter?.photo_url ? (
                    <img
                      src={`${BACKEND_URL}${meter.photo_url}`}
                      alt={meter.name}
                      className="max-h-full max-w-full object-contain"
                    />
                  ) : (
                    <svg className="w-14 h-14 text-gray-400 dark:text-gray-500" viewBox="0 0 100 100" fill="currentColor">
                      <rect x="20" y="25" width="15" height="50" rx="2" />
                      <rect x="42" y="15" width="15" height="60" rx="2" />
                      <rect x="64" y="35" width="15" height="40" rx="2" />
                    </svg>
                  )}
                </div>
                <h2 className={`text-lg font-bold text-center mt-2 leading-tight ${textPrimary}`}>{meter?.name || t('energyMeter')}</h2>
              </div>

              {/* Device Information */}
              <div className="px-4 py-3 pb-4">
                <h3 className={`font-semibold ${textPrimary} mb-3 text-sm`}>Device Information</h3>

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between items-center">
                    <span className={textSecondary}>State:</span>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${isStale ? 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400' : 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400'}`}>
                      {isStale ? t('offline') : t('online')}
                    </span>
                  </div>

                  <div className="flex justify-between">
                    <span className={textSecondary}>Plant:</span>
                    <span className={textPrimary}>{currentCompany?.name || dataLogger?.name || 'N/A'}</span>
                  </div>

                  {meter?.brand != null && String(meter.brand).trim() !== '' && (
                    <div className="flex justify-between">
                      <span className={textSecondary}>{t('brand')}:</span>
                      <span className={textPrimary}>{energyMeterBrandFromApi(meter.brand)}</span>
                    </div>
                  )}

                  <div className="flex justify-between">
                    <span className={textSecondary}>Model:</span>
                    <span className={textPrimary}>{displayEnergyMeterModelUi(meter)}</span>
                  </div>

                  {/* <div className="flex justify-between">
                    <span className={textSecondary}>IP:</span>
                    <span className={textPrimary}>{meter?.ip_address != null && String(meter.ip_address).trim() !== '' ? String(meter.ip_address) : '—'}</span>
                  </div>

                  <div className="flex justify-between">
                    <span className={textSecondary}>Unit ID:</span>
                    <span className={textPrimary}>{meter?.unit_id != null && meter?.unit_id !== '' ? String(meter.unit_id) : '—'}</span>
                  </div>

                  <div className="flex justify-between">
                    <span className={textSecondary}>Port:</span>
                    <span className={textPrimary}>{meter?.port != null && meter?.port !== '' ? String(meter.port) : '—'}</span>
                  </div> */}

                  {dataLogger != null && (
                    <div className="flex justify-between">
                      <span className={textSecondary}>Data Logger:</span>
                      <span className={textPrimary}>{dataLogger?.serial_number || 'N/A'}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* {isHibEnergyFlowCompany(currentCompany) && (
                <div className={`mt-auto flex-shrink-0 border-t p-2 pt-3 ${borderColor}`}>
                  <EnergyPowerFlowDiagram
                    isDark={isDark}
                    displayPowerUnit={meter?.display_power_unit}
                    {...powerFlowKw}
                  />
                </div>
              )} */}
            </div>
          </div>

          {/* Right Panel */}
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden p-4">
            {/* Main Tabs: Details | Real Time | Alarms | IN-OUT */}
            <div className="flex border-b mb-4 gap-1" style={{ borderColor: isDark ? '#334155' : '#e5e7eb' }}>
              <button onClick={() => setMainTab('details')} className={`px-4 py-2 text-sm font-medium ${mainTab === 'details' ? 'border-b-2 border-purple-500 text-purple-600 dark:text-purple-400' : `${textSecondary}`}`}>{t('details')}</button>
              <button onClick={() => setMainTab('realtime')} className={`px-4 py-2 text-sm font-medium ${mainTab === 'realtime' ? 'border-b-2 border-purple-500 text-purple-600 dark:text-purple-400' : `${textSecondary}`}`}>{t('realTime')}</button>
              <button onClick={() => setMainTab('alarms')} className={`px-4 py-2 text-sm font-medium flex items-center gap-1.5 ${mainTab === 'alarms' ? 'border-b-2 border-purple-500 text-purple-600 dark:text-purple-400' : `${textSecondary}`}`}>{t('alarms')} <span className={`text-xs px-1.5 py-0.5 rounded ${(getDisplayAlarms(alarms).filter((a) => String(a?.state).toUpperCase() === 'ON').length || 0) > 0 ? 'bg-red-500/80 text-white' : 'bg-gray-200 dark:bg-slate-600 text-gray-600 dark:text-gray-400'}`}>{getDisplayAlarms(alarms).filter((a) => String(a?.state).toUpperCase() === 'ON').length ?? 0}</span></button>
              <button onClick={() => setMainTab('inout')} className={`px-4 py-2 text-sm font-medium ${mainTab === 'inout' ? 'border-b-2 border-purple-500 text-purple-600 dark:text-purple-400' : `${textSecondary}`}`}>{t('inOut')}</button>
            </div>

            {mainTab === 'alarms' ? (
              <div className={`flex-1 rounded-xl border p-4 overflow-y-auto ${bgCard}`} style={{ borderColor: isDark ? '#334155' : '#f3f4f6' }}>
                {alarmsLoading ? (
                  <div className={`py-16 text-center text-sm ${textSecondary}`}>{t('loading')}</div>
                ) : (
                  <>
                <h3 className={`font-semibold ${textPrimary} mb-4`}>{t('alarms')} ({getDisplayAlarms(alarms).filter((a) => String(a?.state).toUpperCase() === 'ON').length}/{getDisplayAlarms(alarms).length})</h3>
                <ul className="grid grid-cols-2 gap-x-4 gap-y-1 mb-6">
                  {getDisplayAlarms(alarms).map((a, i) => {
                    const state = (a?.state ?? 'OFF').toUpperCase();
                    const isOn = state === 'ON';
                    return (
                      <li key={a?.no ?? i} className={`flex items-center py-3 px-3 rounded-lg border ${borderColor} text-sm ${isOn ? 'bg-red-50/50 dark:bg-red-900/10 border-red-200 dark:border-red-900/30' : ''}`}>
                        <span className={isOn ? 'text-red-700 dark:text-red-300 font-medium' : textPrimary}>{formatAlarmDisplay(a)}</span>
                      </li>
                    );
                  })}
                </ul>
                {alarmHistory.length > 0 && (
                  <>
                    <h3 className={`text-sm font-semibold ${textPrimary} mb-2`}>{t('alarmHistory') || 'Histori alarmeve'}</h3>
                    <div className="rounded-lg border overflow-hidden" style={{ borderColor: isDark ? '#334155' : '#e5e7eb' }}>
                      <table className="w-full text-sm">
                        <thead className={isDark ? 'bg-slate-800/50' : 'bg-gray-50'}>
                          <tr><th className={`px-3 py-2 text-left font-medium ${textSecondary}`}>Data/ora</th><th className={`px-3 py-2 text-left font-medium ${textSecondary}`}>Status</th></tr>
                        </thead>
                        <tbody className={textPrimary}>
                          {alarmHistory.slice(0, 30).map((h, i) => (
                            <tr key={i} className={i % 2 === 0 ? (isDark ? 'bg-[#1e293b]' : 'bg-white') : (isDark ? 'bg-slate-800/30' : 'bg-gray-50/50')}>
                              <td className="px-3 py-2 whitespace-nowrap">{h.ts ? new Date(h.ts).toLocaleString('sq-AL', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                              <td className={`px-3 py-2 font-medium ${h.active ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}`}>{h.active ? (t('alarmActive') || 'Aktiv') : (t('alarmInactive') || 'Jo aktiv')}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
                  </>
                )}
              </div>
            ) : mainTab === 'inout' ? (
              <div className="flex-1 overflow-y-auto">
                <DigitalIOSection meter={meter} readings={readings} rawPayload={rawPayload} isDark={isDark} fetchData={fetchData} t={t} />
              </div>
            ) : mainTab === 'details' ? (
              <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden">
                {/* 4 Main Cards */}
                <div className="grid shrink-0 grid-cols-2 gap-4 md:grid-cols-4">
                  <div className="rounded-xl p-4 border shadow-sm" style={{ backgroundColor: bgCard, borderColor: isDark ? '#334155' : '#f3f4f6' }}>
                    <p className={`text-2xl font-bold ${textPrimary}`}>{safeToFixed(useKw ? (typeof activePowerMW === 'number' ? Math.abs(activePowerMW) : Math.abs(Number(activePowerMW || 0))) * 1000 : (typeof activePowerMW === 'number' ? Math.abs(activePowerMW) : Math.abs(Number(activePowerMW || 0))), 2)} <span className="text-base font-normal">{emLabels.power}</span></p>
                    <p className={`text-sm ${textSecondary} mt-1`}>{t('activePowerReal')}</p>
                  </div>
                  <div className="rounded-xl p-4 border shadow-sm" style={{ backgroundColor: bgCard, borderColor: isDark ? '#334155' : '#f3f4f6' }}>
                    <p className={`text-2xl font-bold ${textPrimary}`}>{todayEnergy != null ? safeToFixed(useKw ? Number(todayEnergy) * 1000 : Number(todayEnergy), useKw ? 1 : 3) : '—'} <span className="text-base font-normal">{emLabels.energy}</span></p>
                    <p className={`text-sm ${textSecondary} mt-1`}>{t('todayEnergy')}</p>
                  </div>
                  <div className="rounded-xl p-4 border shadow-sm" style={{ backgroundColor: bgCard, borderColor: isDark ? '#334155' : '#f3f4f6' }}>
                    <p className={`text-2xl font-bold ${textPrimary}`}>{monthlyEnergy != null ? safeToFixed(useKw ? Number(monthlyEnergy) * 1000 : Number(monthlyEnergy), useKw ? 1 : 3) : '—'} <span className="text-base font-normal">{emLabels.energy}</span></p>
                    <p className={`text-sm ${textSecondary} mt-1`}>{t('monthlyEnergy')}</p>
                  </div>
                  <div className="rounded-xl p-4 border shadow-sm" style={{ backgroundColor: bgCard, borderColor: isDark ? '#334155' : '#f3f4f6' }}>
                    <p className={`text-2xl font-bold ${textPrimary}`}>{totalEnergy != null ? safeToFixed(useKw ? Number(totalEnergy) * 1000 : Number(totalEnergy), useKw ? 1 : 3) : '—'} <span className="text-base font-normal">{emLabels.energy}</span></p>
                    <p className={`text-sm ${textSecondary} mt-1`}>{t('totalEnergyLabel')}</p>
                  </div>
                </div>
                {/* Meter Details — mbush hapësirën e mbetur vertikalisht */}
                <div
                  className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border p-4"
                  style={{ backgroundColor: bgCard, borderColor: isDark ? '#334155' : '#f3f4f6' }}
                >
                  <h3 className={`mb-3 shrink-0 text-center font-semibold ${textPrimary}`}>{t('meterDetails')}</h3>
                  <div className="min-h-0 flex-1 overflow-y-auto">
                    <MeterDetailsAccordion readings={safeReadings} isDark={isDark} isWeb useKw={useKw} t={t} />
                  </div>
                </div>
              </div>
            ) : (
              <>
                {/* Chart Area - Energy Analysis (Export only) - Real Time */}
                <div className="flex-1 rounded-xl border shadow-sm p-4 overflow-hidden flex flex-col" style={{ backgroundColor: bgCard, borderColor: isDark ? '#334155' : '#f3f4f6' }}>
                  {/* Chart Header */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 flex-shrink-0">
                    <div className="flex items-center gap-2">
                      <div className="w-1 h-5 bg-purple-500 rounded"></div>
                      <h3 className={`font-semibold ${textPrimary}`}>{t('energyAnalysisLabel')}</h3>
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                      {/* Period buttons */}
                      <div className={`flex rounded-lg p-1 ${isDark ? 'bg-[#334155]' : 'bg-gray-100'}`}>
                        {['Day', 'Month', 'Year', 'Total'].map((period) => (
                          <button
                            key={period}
                            onClick={() => setGraphPeriod?.(period)}
                            className={`px-4 py-1.5 rounded-md text-sm font-medium transition ${graphPeriod === period
                              ? isDark ? 'bg-[#1e293b] text-purple-400 shadow-sm' : 'bg-white text-purple-600 shadow-sm'
                              : isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'
                              }`}
                          >
                            {period}
                          </button>
                        ))}
                      </div>
                      {/* Date nav + Export */}
                      <div className="flex items-center gap-2">
                        {(graphPeriod === 'Day' || graphPeriod === 'Month' || graphPeriod === 'Year') && (
                          <>
                            <input
                              ref={datePickerRef}
                              type="date"
                              max={todayStr()}
                              value={selectedDate || todayStr()}
                              onChange={(e) => e.target.value && setSelectedDate?.(e.target.value)}
                              className="sr-only"
                              tabIndex={-1}
                              aria-hidden
                            />
                            <input
                              ref={monthPickerRef}
                              type="month"
                              max={todayStr().slice(0, 7)}
                              value={(selectedDate || todayStr()).slice(0, 7)}
                              onChange={(e) => e.target.value && setSelectedDate?.(`${e.target.value}-01`)}
                              className="sr-only"
                              tabIndex={-1}
                              aria-hidden
                            />
                            <button type="button" onClick={() => changeDate(-1)} className={`p-1.5 rounded ${textSecondary} hover:bg-gray-100 dark:hover:bg-slate-700`} aria-label="Previous">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                            </button>
                            {graphPeriod === 'Year' ? (
                              <select
                                value={(selectedDate || todayStr()).slice(0, 4)}
                                onChange={(e) => setSelectedDate?.(`${e.target.value}-01-01`)}
                                className={`text-sm min-w-[4.5rem] rounded-md border px-2 py-1 ${isDark ? 'bg-[#1e293b] border-slate-600 text-white' : 'bg-white border-gray-200 text-gray-900'}`}
                              >
                                {Array.from({ length: Math.max(1, new Date().getFullYear() - 2019) }, (_, i) => 2020 + i)
                                  .filter((y) => y <= new Date().getFullYear())
                                  .map((y) => (
                                    <option key={y} value={y}>{y}</option>
                                  ))}
                              </select>
                            ) : (
                              <button
                                type="button"
                                onClick={openWebPeriodPicker}
                                className={`text-sm ${textPrimary} min-w-[5.5rem] text-center px-2 py-1 rounded-md hover:bg-gray-100 dark:hover:bg-slate-700`}
                              >
                                {graphDateDisplay}
                              </button>
                            )}
                            <button type="button" onClick={() => changeDate(1)} className={`p-1.5 rounded ${textSecondary} hover:bg-gray-100 dark:hover:bg-slate-700`} aria-label="Next">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => {
                            const header = graphPeriod === 'Day' ? 'Time,Avg kW (5min),Delta GWh' : 'Period,Export GWh';
                            const rows = graphPeriod === 'Day'
                              ? (chartData || []).map((d) => `${d.name},${Number(d.value).toFixed(4)},${d.valueMwh != null ? (Number(d.valueMwh) / 1000).toFixed(6) : ''}`)
                              : (chartData || []).map((d) => `${d.name},${(Number(d.value) / 1000).toFixed(6)}`);
                            const blob = new Blob([`${header}\n${rows.join('\n')}`], { type: 'text/csv' });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `energy-export-${(typeof graphPeriod === 'string' ? graphPeriod : 'Day')}-${Date.now()}.csv`;
                            a.click();
                            URL.revokeObjectURL(url);
                          }}
                          className={`p-1.5 rounded ${textSecondary} hover:bg-gray-100 dark:hover:bg-slate-700`}
                          title={t('exportLabel')}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Line Chart - Export only */}
                  <div className="flex-1 min-h-[200px] relative">
                    {chartLoading && (
                      <div className="absolute inset-0 flex items-center justify-center bg-white/80 dark:bg-[#0f172a]/80 z-10 rounded-lg">
                        <div className="flex flex-col items-center gap-2">
                          <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
                          <span className="text-sm text-gray-600 dark:text-gray-400">{t('loading')}</span>
                        </div>
                      </div>
                    )}
                    <ResponsiveContainer width="100%" height="100%">
                      {graphPeriod === 'Day' ? (
                        <EmRealtimeEnergyDayChart
                          chartData={chartData}
                          visible={
                            emEnergyAnalysisHib
                              ? emAnalysisSeriesVisibleDeferred ?? emAnalysisSeriesVisible
                              : EM_ANALYSIS_VISIBLE_EXPORT_ONLY
                          }
                          isDark={isDark}
                          t={t}
                          displayUnit={meter?.display_power_unit}
                          seriesList={emAnalysisSeriesList}
                        />
                      ) : (
                        <BarChart data={chartData || []} margin={{ top: 24, right: 20, left: 12, bottom: 28 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#334155' : '#f0f0f0'} vertical={false} />
                          <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: isDark ? '#9ca3af' : '#6b7280', fontSize: 12, angle: -45, textAnchor: 'end' }} interval="preserveStartEnd" />
                          <YAxis axisLine={false} tickLine={false} width={58} tick={{ fill: isDark ? '#9ca3af' : '#6b7280', fontSize: 12 }} tickFormatter={(v) => chartEnergyDeltaTickLabel(v, meter?.display_power_unit)} />
                          <Tooltip
                            contentStyle={{ backgroundColor: isDark ? '#1e293b' : '#fff', border: `1px solid ${isDark ? '#334155' : '#e5e7eb'}`, borderRadius: '8px', color: isDark ? '#fff' : '#000' }}
                            formatter={(v) => [chartEnergyDeltaTickLabel(v, meter?.display_power_unit), 'Export']}
                          />
                          <Bar dataKey="value" fill="#a855f7" radius={[5, 5, 0, 0]} />
                        </BarChart>
                      )}
                    </ResponsiveContainer>
                  </div>
                  {graphPeriod === 'Day' && emEnergyAnalysisHib && (
                    <>
                      <EmEnergyAnalysisLegend
                        visible={emAnalysisSeriesVisible}
                        onToggle={onToggleEmAnalysisSeries}
                        isDark={isDark}
                        t={t}
                        seriesList={emAnalysisSeriesList}
                      />
                      <EmEnergyAnalysisSummaryBars
                        graphPeriod={graphPeriod}
                        chartData={chartData}
                        visible={emAnalysisSeriesVisibleDeferred ?? emAnalysisSeriesVisible}
                        t={t}
                        isDark={isDark}
                      />
                    </>
                  )}
                </div>

              </>
            )}
          </div>
        </div>

        {/* Photo Upload Modal */}
        {showPhotoModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="rounded-2xl w-full max-w-sm p-4" style={{ backgroundColor: bgCard }}>
              <div className="flex items-center justify-between mb-3">
                <h3 className={`text-base md:text-lg font-semibold ${textPrimary}`}>
                  {t('photoUploadFor')} "{meter?.name}"
                </h3>
                <button type="button" onClick={() => setShowPhotoModal(false)} className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400" aria-label={t('close')}>✕</button>
              </div>
              <input type="file" ref={fileInputRef} onChange={handlePhotoUpload} accept="image/*" className="hidden" />
              <div className="flex gap-2">
                <button onClick={() => setShowPhotoModal(false)}
                  className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium ${isDark ? 'bg-[#334155] text-white' : 'bg-gray-100 text-gray-700'}`} disabled={uploading}>
                  {t('cancel')}
                </button>
                <button onClick={() => fileInputRef.current?.click()}
                  className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg text-sm font-medium disabled:opacity-50" disabled={uploading}>
                  {uploading ? t('loading') : t('selectPhoto')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================
const EnergyMeterDetail = () => {
  const { deviceId, meterId } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [meter, setMeter] = useState(null);
  const [dataLogger, setDataLogger] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [readingsList, setReadingsList] = useState([]);
  const [todayEnergyApi, setTodayEnergyApi] = useState(null);
  const [monthlyEnergyApi, setMonthlyEnergyApi] = useState(null);
  const [chartReadings, setChartReadings] = useState(null);
  const [chartLoading, setChartLoading] = useState(false);
  /** Shuma e fuqisë aktive (kW) nga të gjithë inverterët e kompanisë — për Ppv në diagramë. */
  const [plantInverterSumKw, setPlantInverterSumKw] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDark, setIsDark] = useState(document.documentElement.classList.contains('dark'));
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [alarms, setAlarms] = useState([]);
  const [alarmHistory, setAlarmHistory] = useState([]);
  const [alarmsLoading, setAlarmsLoading] = useState(false);
  const [mainTab, setMainTab] = useState('details');

  const emStorageKey = meterId ? `chart-em-${meterId}` : null;
  const [graphPeriod, setGraphPeriod] = useState(() => {
    if (typeof window !== 'undefined' && emStorageKey) {
      try {
        const s = localStorage.getItem(emStorageKey);
        if (s) {
          const p = JSON.parse(s);
          if (p?.graphPeriod) return p.graphPeriod;
        }
      } catch (_) { }
    }
    return 'Day';
  });
  const [selectedDate, setSelectedDate] = useState(() => {
    if (typeof window !== 'undefined' && emStorageKey) {
      try {
        const s = localStorage.getItem(emStorageKey);
        if (s) {
          const p = JSON.parse(s);
          if (p?.selectedDate) return p.selectedDate;
        }
      } catch (_) { }
    }
    return todayStr();
  });

  useEffect(() => {
    if (emStorageKey && typeof window !== 'undefined') {
      try {
        localStorage.setItem(emStorageKey, JSON.stringify({ graphPeriod, selectedDate }));
      } catch (_) { }
    }
  }, [emStorageKey, graphPeriod, selectedDate]);

  const emAnalysisLsKey = meterId ? `em-energy-analysis-series-${meterId}` : null;
  const [emAnalysisSeriesVisible, setEmAnalysisSeriesVisible] = useState(() => defaultEmAnalysisSeriesVisible());
  const emAnalysisSeriesVisibleDeferred = useDeferredValue(emAnalysisSeriesVisible);

  useEffect(() => {
    if (!emAnalysisLsKey || typeof window === 'undefined') return;
    try {
      const raw = localStorage.getItem(emAnalysisLsKey);
      if (!raw) return;
      const p = JSON.parse(raw);
      if (p && typeof p === 'object') {
        setEmAnalysisSeriesVisible({ ...defaultEmAnalysisSeriesVisible(), ...p });
      }
    } catch (_) { /* ignore */ }
  }, [emAnalysisLsKey]);

  const emAnalysisLsDebounceRef = useRef(null);
  useEffect(() => {
    if (!emAnalysisLsKey || typeof window === 'undefined') return;
    if (emAnalysisLsDebounceRef.current) clearTimeout(emAnalysisLsDebounceRef.current);
    emAnalysisLsDebounceRef.current = setTimeout(() => {
      emAnalysisLsDebounceRef.current = null;
      try {
        localStorage.setItem(emAnalysisLsKey, JSON.stringify(emAnalysisSeriesVisible));
      } catch (_) { /* ignore */ }
    }, 350);
    return () => {
      if (emAnalysisLsDebounceRef.current) clearTimeout(emAnalysisLsDebounceRef.current);
    };
  }, [emAnalysisLsKey, emAnalysisSeriesVisible]);

  const toggleEmAnalysisSeries = useCallback((seriesId) => {
    setEmAnalysisSeriesVisible((v) => ({ ...v, [seriesId]: !v[seriesId] }));
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);

    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains('dark'));
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });

    return () => {
      window.removeEventListener('resize', handleResize);
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    setAlarms([]);
    setAlarmHistory([]);
    setAlarmsLoading(false);
    setMainTab('details');
  }, [meterId]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchData defined below; meterId/deviceId drive reload
  }, [meterId, deviceId]);

  // Energjia Sot dhe Energjia Mujore (1–deri sot): nga API, përditësohen çdo 5 min
  useEffect(() => {
    if (!meterId) return;
    const getToken = () => localStorage.getItem('access_token');
    const headers = { Authorization: `Bearer ${getToken()}` };
    const fetchAggregates = () => {
      Promise.all([
        axios.get(`${BACKEND_URL}/api/devices/${meterId}/energy-meter-today`, { headers }),
        axios.get(`${BACKEND_URL}/api/devices/${meterId}/energy-meter-month-to-date`, { headers }),
      ])
        .then(([todayRes, monthRes]) => {
          const tN = Number(todayRes.data?.today_energy_mwh);
          const mN = Number(monthRes.data?.month_to_date_energy_mwh);
          setTodayEnergyApi(Number.isFinite(tN) ? tN : null);
          setMonthlyEnergyApi(Number.isFinite(mN) ? mN : null);
        })
        .catch(() => {
          setTodayEnergyApi(null);
          setMonthlyEnergyApi(null);
        });
    };
    fetchAggregates();
    const iv = setInterval(fetchAggregates, 5 * 60 * 1000); // 5 min
    return () => clearInterval(iv);
  }, [meterId]);

  // Cache i shkurtër për grafik – 2 min (eviton refetch, më pak ngarkesë API/mobile)
  const chartCacheRef = useRef({ key: '', data: null, ts: 0 });

  // Për grafik: fetch me debounce + anulim, pa overload në API / mobile
  useEffect(() => {
    if (!meterId || !selectedDate) return;
    const sel = new Date(selectedDate);
    let dateFrom = '', dateTo = '';
    if (graphPeriod === 'Day') {
      dateFrom = `${selectedDate}T00:00:00`;
      dateTo = `${selectedDate}T23:59:59`;
    } else if (graphPeriod === 'Month') {
      const y = sel.getFullYear(), m = sel.getMonth();
      dateFrom = `${y}-${String(m + 1).padStart(2, '0')}-01T00:00:00`;
      const lastDay = new Date(y, m + 1, 0).getDate();
      dateTo = `${y}-${String(m + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}T23:59:59`;
    } else if (graphPeriod === 'Year') {
      dateFrom = `${sel.getFullYear()}-01-01T00:00:00`;
      dateTo = `${sel.getFullYear()}-12-31T23:59:59`;
    } else if (graphPeriod === 'Total') {
      const now = new Date();
      dateFrom = '2020-01-01T00:00:00';
      dateTo = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}T23:59:59`;
    } else {
      setChartReadings(null);
      return;
    }
    const chartKey = `${meterId}|${dateFrom}|${dateTo}`;
    const cached = chartCacheRef.current;
    if (cached.key === chartKey && cached.data !== null && Date.now() - cached.ts < CHART_CACHE_TTL_MS) {
      setChartReadings(cached.data);
      return;
    }
    setChartLoading(true);
    const controller = new AbortController();
    const timer = setTimeout(() => {
      const getToken = () => localStorage.getItem('access_token');
      axios
        .get(
          `${BACKEND_URL}/api/devices/${meterId}/pi-energy-meter-readings?date_from=${encodeURIComponent(dateFrom)}&date_to=${encodeURIComponent(dateTo)}`,
          { headers: { Authorization: `Bearer ${getToken()}` }, signal: controller.signal },
        )
        .then((res) => {
          const data = res.data?.readings ?? [];
          chartCacheRef.current = { key: chartKey, data, ts: Date.now() };
          setChartReadings(data);
          setChartLoading(false);
        })
        .catch((err) => {
          setChartLoading(false);
          if (axios.isCancel(err)) return;
          setChartReadings([]);
        });
    }, 200);
    return () => {
      clearTimeout(timer);
      controller.abort();
      setChartLoading(false);
    };
  }, [meterId, selectedDate, graphPeriod]);

  const getToken = () => localStorage.getItem('access_token');

  const loadAlarms = useCallback(async (signal) => {
    if (!meterId) return;
    const headers = { Authorization: `Bearer ${localStorage.getItem('access_token')}` };
    setAlarmsLoading(true);
    try {
      const [alarmsPiRes, alarmsApiRes] = await Promise.all([
        axios
          .get(`${BACKEND_URL}/api/devices/${meterId}/pi-alarms-energy-meter?limit=50`, { headers, signal })
          .catch(() => ({ data: { alarms: [] } })),
        axios
          .get(`${BACKEND_URL}/api/alarms?device_id=${meterId}&device_type=ENERGY_METER&limit=50`, { headers, signal })
          .catch(() => ({ data: [] })),
      ]);
      if (signal.aborted) return;
      const { alarms: merged, alarmHistory: history } = mergeEnergyMeterAlarmResponses(alarmsPiRes, alarmsApiRes);
      setAlarms(merged);
      setAlarmHistory(history);
    } catch {
      if (!signal.aborted) {
        setAlarms([]);
        setAlarmHistory([]);
      }
    } finally {
      if (!signal.aborted) setAlarmsLoading(false);
    }
  }, [meterId]);

  useEffect(() => {
    if (mainTab !== 'alarms' || !meterId) return;
    const ac = new AbortController();
    loadAlarms(ac.signal);
    return () => ac.abort();
  }, [mainTab, meterId, loadAlarms]);

  const fetchData = async () => {
    if (!meterId) return;
    try {
      const headers = { Authorization: `Bearer ${getToken()}` };
      const isStandalone = deviceId === 'standalone';
      const requests = [
        cachedAxiosGet(axios, `${BACKEND_URL}/api/devices/energy-meters/${meterId}`, { headers }, { ttlMs: 90_000 }).catch(() => ({ data: null })),
        isStandalone ? Promise.resolve({ data: null }) : cachedAxiosGet(axios, `${BACKEND_URL}/api/devices/data-loggers/${deviceId}`, { headers }, { ttlMs: 90_000 }),
        cachedAxiosGet(axios, `${BACKEND_URL}/api/companies`, { headers }, { ttlMs: 120_000 }),
        axios.get(`${BACKEND_URL}/api/devices/${meterId}/pi-energy-meter-readings?limit=300`, { headers }).catch(() => ({ data: { readings: [] } })),
      ];
      const [meterRes, dataLoggerRes, companiesRes, readingsRes] = await Promise.all(requests);

      const foundMeter = meterRes.data ?? null;
      setMeter(foundMeter);
      setDataLogger(dataLoggerRes?.data ?? null);
      setCompanies(companiesRes.data);
      setReadingsList(readingsRes.data?.readings ?? []);

      let plantKw = null;
      if (foundMeter?.company_id) {
        try {
          const invRes = await axios.get(`${BACKEND_URL}/api/devices/inverters`, {
            headers,
            params: { company_id: foundMeter.company_id },
          });
          const invs = Array.isArray(invRes.data) ? invRes.data : [];
          if (invs.length === 0) {
            plantKw = 0;
          } else {
            let sumListed = 0;
            const needReadings = [];
            for (const inv of invs) {
              const pk = inv?.power_kw;
              if (pk != null && pk !== '' && Number.isFinite(Number(pk))) {
                sumListed += Number(pk);
              } else {
                needReadings.push(inv);
              }
            }
            if (needReadings.length === 0) {
              plantKw = sumListed;
            } else {
              const parts = await Promise.all(
                needReadings.map((inv) =>
                  axios
                    .get(`${BACKEND_URL}/api/devices/${inv.id}/pi-inverter-readings`, {
                      headers,
                      params: { limit: 1 },
                    })
                    .then((r) => getInverterActivePowerKw(r.data?.readings?.[0]?.payload))
                    .catch(() => 0),
                ),
              );
              plantKw = sumListed + parts.reduce((a, b) => a + b, 0);
            }
          }
        } catch {
          plantKw = null;
        }
      }
      setPlantInverterSumKw(plantKw);
    } catch (error) {
      console.error('Error fetching data:', error);
      setPlantInverterSumKw(null);
    } finally {
      setLoading(false);
    }
  };

  const readingsForChart = chartReadings !== null ? chartReadings : readingsList;
  const chartData = useMemo(
    () => buildEnergyMeterChartData(readingsForChart, graphPeriod, selectedDate, meter, plantInverterSumKw),
    [readingsForChart, graphPeriod, selectedDate, meter, plantInverterSumKw],
  );

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">{t('loading')}</div>
        </div>
      </Layout>
    );
  }

  if (!meter) {
    return (
      <Layout>
        <div className="p-6 text-center">
          <p className="text-xl text-red-600">{t('energyMeterNotFound')}</p>
          <button onClick={() => navigate(`/devices`)} className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg">
            {t('back')}
          </button>
        </div>
      </Layout>
    );
  }

  if (isChintEnergyMeter(meter)) {
    const chintTo = buildEnergyMeterDetailPath(meter);
    if (chintTo) return <Navigate to={chintTo} replace />;
  }

  const latestReading = readingsList.length > 0 ? readingsList[0] : null;
  const readings = buildReadingsFromPayload(latestReading?.payload, meter);
  const lastUpdated = formatTimeAgo(latestReading?.ts) || null;

  const ageSec = latestReading?.ts ? Math.floor((Date.now() - new Date(latestReading.ts).getTime()) / 1000) : 999999;
  const isStale = ageSec > STALE_READING_SEC;
  const activePowerMW = getActivePowerMW(latestReading?.payload, meter);
  const emUseKw = normalizeDisplayPowerUnit(meter?.display_power_unit) === 'kw';
  const todayEnergy = todayEnergyApi != null ? todayEnergyApi : computeEnergyFromReadings(readingsList, 'today', meter);
  const monthlyEnergy = monthlyEnergyApi != null ? monthlyEnergyApi : computeEnergyFromReadings(readingsList, 'month', meter);
  const totalEnergy = getGridExportEnergyMwhFromPayload(latestReading?.payload, meter);

  const powerFlowKw = buildPowerFlowDiagramValues(meter, latestReading?.payload, plantInverterSumKw);

  const detailViewProps = {
    meter,
    dataLogger,
    companies,
    navigate,
    isDark,
    readings,
    lastUpdated,
    readingsList,
    isStale,
    activePowerMW,
    todayEnergy,
    monthlyEnergy,
    totalEnergy,
    chartData,
    chartLoading,
    graphPeriod,
    setGraphPeriod,
    selectedDate,
    setSelectedDate,
    alarms,
    alarmHistory,
    alarmsLoading,
    mainTab,
    setMainTab,
    fetchData,
    rawPayload: latestReading?.payload,
    powerFlowKw,
    useKw: emUseKw,
    emAnalysisSeriesVisible,
    emAnalysisSeriesVisibleDeferred,
    onToggleEmAnalysisSeries: toggleEmAnalysisSeries,
  };

  if (isMobile) {
    return <MobileEnergyMeterView {...detailViewProps} />;
  }

  return <WebEnergyMeterView {...detailViewProps} />;
};

export default EnergyMeterDetail;
